import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { api } from '@/api';
import { toast } from 'sonner';
import {
  Plus, Search, Loader2, FileText, Clock, AlertTriangle,
  ChevronRight, Calendar, ArrowLeft, RefreshCw, Download,
  Upload, CheckCircle2, AlertCircle, Filter, X, FileUp,
  Sparkles, Building, Layers, FileCheck, Send, Archive,
  Edit2, Trash2, ExternalLink, ClipboardList, Save, History,
  Link2, AlertOctagon, Pencil, User, MapPin, CalendarDays,
  MoreHorizontal, ChevronDown, Eye, Copy, FolderOpen, FileSpreadsheet, BarChart3,
  Settings, GitCompare, Info, Wand2, List
} from 'lucide-react';
import { Mail, Building2, Users } from 'lucide-react';
import { WalkthroughTrigger } from '@/components/walkthrough';
import { AICommandModal, ScheduleRegister, ScheduleDetailView } from '@/components/AIScheduleWorkflow';

const STATUS_COLORS = {
  not_requested: 'bg-slate-100 text-slate-700',
  requested: 'bg-blue-100 text-blue-700',
  received: 'bg-green-100 text-green-700',
  under_review: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-emerald-100 text-emerald-700',
  revised: 'bg-purple-100 text-purple-700',
  outstanding: 'bg-orange-100 text-orange-700',
  late: 'bg-red-100 text-red-700',
  closed: 'bg-slate-200 text-slate-600'
};

const DISCIPLINE_COLORS = {
  structural: 'bg-orange-100 text-orange-700',
  architectural: 'bg-blue-100 text-blue-700',
  electrical: 'bg-yellow-100 text-yellow-700',
  mechanical: 'bg-cyan-100 text-cyan-700',
  plumbing: 'bg-indigo-100 text-indigo-700',
  fire: 'bg-red-100 text-red-700',
  civil: 'bg-amber-100 text-amber-700',
  facade: 'bg-purple-100 text-purple-700',
  coordination: 'bg-pink-100 text-pink-700',
  general: 'bg-slate-100 text-slate-700'
};

export default function IRSSchedulePage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = window.location;

  // Programme selection (from Programs module)
  const [programmeUploads, setProgrammeUploads] = useState([]);
  const [selectedProgrammeId, setSelectedProgrammeId] = useState('');

  // Data state
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [disciplineFilter, setDisciplineFilter] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [showCritical, setShowCritical] = useState(false);
  const [showLate, setShowLate] = useState(false);

  // AI Command
  const [aiCommand, setAiCommand] = useState('');
  const [aiProcessing, setAiProcessing] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  // Dialogs
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [detailTab, setDetailTab] = useState('details');
  const [changeReason, setChangeReason] = useState('');

  // Drawing linking state
  const [drawingSearchOpen, setDrawingSearchOpen] = useState(false);
  const [linkingDrawingItemId, setLinkingDrawingItemId] = useState(null);
  const [availableDrawings, setAvailableDrawings] = useState([]);
  const [loadingDrawings, setLoadingDrawings] = useState(false);
  const [drawingSearch, setDrawingSearch] = useState('');

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newItem, setNewItem] = useState({
    description: '',
    discipline: 'general',
    information_category: 'general',
    area: '',
    level: '',
    zone: '',
    date_information_required: '',
    consultant: '',
    comments: ''
  });

  // Lead Time Configuration
  const [leadTimeConfigOpen, setLeadTimeConfigOpen] = useState(false);
  const [leadTimeConfig, setLeadTimeConfig] = useState(null);
  const [savingLeadTimes, setSavingLeadTimes] = useState(false);

  // Programme Comparison
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [compareProgramme1, setCompareProgramme1] = useState(null);
  const [compareProgramme2, setCompareProgramme2] = useState(null);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [aiCommandModalOpen, setAiCommandModalOpen] = useState(false);
  const [irsActiveView, setIrsActiveView] = useState('schedules'); // 'schedules' | 'schedule-detail'
  const [selectedIrsSchedule, setSelectedIrsSchedule] = useState(null);
  const [scheduleItems, setScheduleItems] = useState([]);
  const [pendingBgJobs, setPendingBgJobs] = useState([]);
  const [markReceivedOpen, setMarkReceivedOpen] = useState(false);
  const [markReceivedItem, setMarkReceivedItem] = useState(null);
  const [receivedDate, setReceivedDate] = useState('');
  const [receivedNotes, setReceivedNotes] = useState('');
  const [receivedType, setReceivedType] = useState('received');

  // Grouping state - collapsed by default
  const [expandedProgrammes, setExpandedProgrammes] = useState({});
  const [expandedTrades, setExpandedTrades] = useState({});

  // Share dialog state (saved schedules)
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareRecipients, setShareRecipients] = useState([]);
  const [shareEmail, setShareEmail] = useState('');
  const [shareName, setShareName] = useState('');
  const [shareRole, setShareRole] = useState('');
  const [sharingInProgress, setSharingInProgress] = useState(false);
  const [shareSendType, setShareSendType] = useState('external');
  const [shareSendEmail, setShareSendEmail] = useState(true);
  const [projectTeamMembers, setProjectTeamMembers] = useState([]);

  // Share published items state
  const [sharePublishedOpen, setSharePublishedOpen] = useState(false);
  const [sharePublishedItems, setSharePublishedItems] = useState([]);
  const [sharePubRecipients, setSharePubRecipients] = useState([]);
  const [sharePubName, setSharePubName] = useState('');
  const [sharePubEmail, setSharePubEmail] = useState('');
  const [sharePubRole, setSharePubRole] = useState('');
  const [sharePubSendType, setSharePubSendType] = useState('external');
  const [sharePubSendEmail, setSharePubSendEmail] = useState(true);
  const [sharePubInProgress, setSharePubInProgress] = useState(false);

  // Group items by Programme -> Trade (two-level hierarchy)
  const groupedItems = useMemo(() => {
    const groups = {};
    items.forEach(item => {
      const progId = item.programme_upload_id || '_ungrouped';
      const prog = programmeUploads.find(p => p.id === progId);
      const progName = prog?.title || (progId === '_ungrouped' ? 'Other Items' : 'Unknown Programme');
      if (!groups[progId]) {
        groups[progId] = { name: progName, trades: {}, count: 0 };
      }
      const trade = item.work_package || item.trade || 'General';
      if (!groups[progId].trades[trade]) {
        groups[progId].trades[trade] = [];
      }
      groups[progId].trades[trade].push(item);
      groups[progId].count++;
    });
    return groups;
  }, [items, programmeUploads]);

  const toggleProgramme = (progId) => {
    setExpandedProgrammes(prev => ({ ...prev, [progId]: !prev[progId] }));
  };

  const toggleTrade = (progId, trade) => {
    const key = `${progId}__${trade}`;
    setExpandedTrades(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Load programmes from Programs module (for dropdown) + legacy uploads
  const loadProgrammes = useCallback(async () => {
    try {
      const [programsRes, legacyRes] = await Promise.all([
        api.get(`/projects/${projectId}/programs`).catch(() => ({ data: { programs: [] } })),
        api.get(`/projects/${projectId}/irs/programme-uploads`).catch(() => ({ data: [] }))
      ]);
      const central = (programsRes.data?.programs || []).map(p => ({
        id: p.id,
        title: p.title || p.filename,
        status: p.status,
        uploaded_at: p.created_at,
        items_count: p.items_count || 0,
      }));
      const legacy = (legacyRes.data || [])
        .filter(u => u.analysis_status === 'completed')
        .map(u => ({
          id: u.id,
          title: u.file_name || u.filename || 'Legacy Programme',
          status: 'completed',
          uploaded_at: u.upload_date || u.uploaded_at,
          items_count: u.items_created || 0,
        }));
      // Merge, deduplicate by id
      const merged = [...central];
      legacy.forEach(l => {
        if (!merged.find(m => m.id === l.id)) merged.push(l);
      });
      setProgrammeUploads(merged);
    } catch (error) {
      console.error('Failed to load programmes:', error);
      toast.error('Failed to load programmes');
    }
  }, [projectId]);

  // Load IRS stats
  const loadStats = useCallback(async () => {
    try {
      const params = selectedProgrammeId ? `?programme_upload_id=${selectedProgrammeId}` : '';
      const statsRes = await api.get(`/projects/${projectId}/irs/stats${params}`);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, [projectId, selectedProgrammeId]);

  // Load Lead Time Configuration
  const loadLeadTimeConfig = useCallback(async () => {
    try {
      const res = await api.get(`/projects/${projectId}/irs/lead-times`);
      setLeadTimeConfig(res.data);
    } catch (error) {
      console.error('Failed to load lead time config:', error);
      // Set defaults if not found
      setLeadTimeConfig({
        structural_drawings: 15,
        reinforcement_details: 10,
        pour_sequences: 7,
        foundation_details: 15,
        floor_plans: 15,
        sections_elevations: 15,
        finishes_layouts: 12,
        door_window_schedules: 10,
        wet_area_details: 12,
        electrical_layouts: 12,
        mechanical_layouts: 15,
        plumbing_layouts: 12,
        fire_layouts: 15,
        services_coordination: 20,
        builders_work: 15,
        interface_drawings: 15,
        earthworks: 10,
        stormwater: 12,
        external_levels: 10,
        default: 15
      });
    }
  }, [projectId]);

  const saveLeadTimeConfig = async () => {
    setSavingLeadTimes(true);
    try {
      await api.put(`/projects/${projectId}/irs/lead-times`, leadTimeConfig);
      toast.success('Lead times updated successfully');
      setLeadTimeConfigOpen(false);
    } catch (error) {
      console.error('Failed to save lead times:', error);
      toast.error('Failed to save lead times');
    } finally {
      setSavingLeadTimes(false);
    }
  };

  // Compare Programmes
  const compareProgrammes = async () => {
    if (!compareProgramme1 || !compareProgramme2) {
      toast.error('Please select two programmes to compare');
      return;
    }
    if (compareProgramme1 === compareProgramme2) {
      toast.error('Please select different programmes');
      return;
    }
    
    setComparing(true);
    try {
      const res = await api.get(`/projects/${projectId}/irs/compare`, {
        params: {
          upload_id_1: compareProgramme1,
          upload_id_2: compareProgramme2
        }
      });
      setComparisonResult(res.data);
    } catch (error) {
      console.error('Failed to compare programmes:', error);
      toast.error('Failed to compare programmes');
    } finally {
      setComparing(false);
    }
  };

  // Load IRS items (optionally filtered by selected programme)
  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (disciplineFilter) params.append('discipline', disciplineFilter);
      if (areaFilter) params.append('level', areaFilter);
      if (selectedProgrammeId) params.append('programme_upload_id', selectedProgrammeId);
      if (showCritical) params.append('critical', 'true');
      if (showLate) params.append('late', 'true');
      params.append('limit', '200');

      const itemsRes = await api.get(`/projects/${projectId}/irs?${params}`);
      setItems(itemsRes.data.items || []);
      setTotal(itemsRes.data.total || 0);
    } catch (error) {
      console.error('Failed to load IRS data:', error);
      toast.error('Failed to load IRS data');
    } finally {
      setLoading(false);
    }
  }, [projectId, selectedProgrammeId, search, statusFilter, disciplineFilter, areaFilter, showCritical, showLate]);

  const loadData = useCallback(async () => {
    await Promise.all([loadItems(), loadStats()]);
  }, [loadItems, loadStats]);

  // Initial load
  useEffect(() => {
    loadProgrammes();
    // Auto-sync: promote items from published schedules that haven't been synced yet
    const autoSyncPublished = async () => {
      try {
        const schedulesRes = await api.get(`/projects/${projectId}/schedule/register?schedule_type=irs`);
        const published = (schedulesRes.data || []).filter(s => s.status === 'published' && (s.items?.length > 0 || true));
        for (const sched of published) {
          // Re-publish to ensure items are promoted (idempotent)
          await api.put(`/projects/${projectId}/schedule/${sched.id}/publish`);
        }
        if (published.length > 0) {
          loadItems();
          loadStats();
        }
      } catch (e) {
        // Silent — don't block page load
      }
    };
    autoSyncPublished();
    // Check for completed background jobs
    api.get(`/projects/${projectId}/background-jobs?status=completed`)
      .then(res => {
        const jobs = (res.data?.jobs || []).filter(j => j.params?.schedule_type === 'irs');
        setPendingBgJobs(jobs);
      })
      .catch(() => {});
  }, [loadProgrammes, projectId]);

  // Load items when programme selection or filters change
  useEffect(() => {
    loadData();
  }, [loadData]);

  // AI Command Handler
  const handleAiCommand = async () => {
    if (!aiCommand.trim()) {
      toast.error('Please enter a command');
      return;
    }
    if (!selectedProgrammeId) {
      toast.error('Please select a programme first');
      return;
    }

    setAiProcessing(true);
    setAiResult(null);

    try {
      const response = await api.post(`/projects/${projectId}/irs/ai-command`, {
        command: aiCommand,
        programme_upload_id: selectedProgrammeId
      });

      setAiResult(response.data);

      if (response.data.success) {
        const results = response.data.results || {};
        if (results.items_updated > 0) {
          toast.success(`Updated ${results.items_updated} items`);
        } else {
          toast.info('Command processed');
        }
        loadData();
      } else {
        toast.error(response.data.error || 'Command failed');
      }
    } catch (error) {
      console.error('AI command error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process command');
    } finally {
      setAiProcessing(false);
    }
  };


  // Load completed background job result into the AI modal for review
  const [bgJobResult, setBgJobResult] = useState(null);
  const handleReviewBgJob = async (jobId) => {
    try {
      const res = await api.get(`/background-jobs/${jobId}`);
      const job = res.data;
      if (job.status === 'completed' && job.result) {
        setBgJobResult(job.result);
        setAiCommandModalOpen(true);
        setPendingBgJobs(prev => prev.filter(j => j.id !== jobId));
      }
    } catch (err) {
      toast.error('Failed to load background job result');
    }
  };

  // Load a schedule and its items
  const openSchedule = async (schedule) => {
    try {
      const res = await api.get(`/projects/${projectId}/schedule/${schedule.id}`);
      setSelectedIrsSchedule(res.data);
      setScheduleItems(res.data.items || []);
      setIrsActiveView('schedule-detail');
    } catch (err) {
      toast.error('Failed to load schedule');
    }
  };

  // Handle per-item actions
  const handleMarkReceived = async () => {
    if (!markReceivedItem || !selectedIrsSchedule) return;
    try {
      await api.post(`/projects/${projectId}/schedule/${selectedIrsSchedule.id}/item/${markReceivedItem.id}/mark-received`, {
        actual_received_date: receivedDate || new Date().toISOString().split('T')[0],
        receipt_type: receivedType,
        notes: receivedNotes,
      });
      // Update locally
      setScheduleItems(prev => prev.map(item =>
        item.id === markReceivedItem.id
          ? { ...item, status: receivedType, actual_received_date: receivedDate || new Date().toISOString().split('T')[0] }
          : item
      ));
      toast.success('Item marked as received');
      setMarkReceivedOpen(false);
      setMarkReceivedItem(null);
      setReceivedDate('');
      setReceivedNotes('');
    } catch (err) {
      toast.error('Failed to mark as received');
    }
  };

  const handleUpdateItemStatus = async (itemId, newStatus) => {
    if (!selectedIrsSchedule) return;
    try {
      await api.put(`/projects/${projectId}/schedule/${selectedIrsSchedule.id}/item/${itemId}`, {
        status: newStatus,
      });
      setScheduleItems(prev => prev.map(item =>
        item.id === itemId ? { ...item, status: newStatus } : item
      ));
      toast.success('Status updated');
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handlePublishSchedule = async () => {
    if (!selectedIrsSchedule) return;
    try {
      const res = await api.put(`/projects/${projectId}/schedule/${selectedIrsSchedule.id}/publish`);
      setSelectedIrsSchedule(prev => ({ ...prev, status: 'published' }));
      toast.success(res.data?.message || 'Schedule published');
      // Reload IRS items list so promoted items appear
      loadItems();
      loadStats();
    } catch (err) {
      toast.error('Failed to publish schedule');
    }
  };

  // Load project team for share dialog
  const loadProjectTeamForShare = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/team`);
      const members = Array.isArray(res.data) ? res.data : (res.data?.members || []);
      const internalRoles = ['admin', 'project_manager', 'contracts_manager', 'quantity_surveyor', 'site_agent', 'foreman', 'safety_officer'];
      const consultantRoles = ['architect', 'structural_engineer', 'electrical_engineer', 'mechanical_engineer', 'engineer', 'consultant', 'project_monitor'];
      const teamList = [];
      for (const u of members) {
        const role = (u.role || '').toLowerCase();
        const name = u.name || u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim();
        let category = 'external';
        let displayRole = u.role || 'Team Member';
        if (consultantRoles.includes(role)) {
          category = 'consultant';
          displayRole = role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        } else if (role === 'subcontractor' || role === 'sub_contractor') {
          category = 'subcontractor';
          displayRole = u.company ? `${u.company} - Subcontractor` : 'Subcontractor';
        } else if (internalRoles.includes(role)) {
          category = 'internal';
          displayRole = role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        }
        teamList.push({
          name: name || u.email,
          email: u.email,
          role: displayRole,
          category,
          is_internal: category === 'internal',
          is_consultant: category === 'consultant',
          is_subcontractor: category === 'subcontractor',
        });
      }
      setProjectTeamMembers(teamList);
    } catch { setProjectTeamMembers([]); }
  };

  // Open share dialog for published IRS items
  const openSharePublished = (itemsToShare) => {
    setSharePublishedItems(itemsToShare);
    setSharePubRecipients([]);
    setSharePubSendType('external');
    setSharePubSendEmail(true);
    loadProjectTeamForShare();
    setSharePublishedOpen(true);
  };

  // Submit share for published IRS items
  const handleSharePublished = async () => {
    if (sharePubRecipients.length === 0) return;
    setSharePubInProgress(true);
    try {
      const itemSummaries = sharePublishedItems.map(item => {
        const name = item.description || item.information_required || 'Item';
        const status = item.status || '-';
        return `  - ${name} (${status})`;
      }).join('\n');
      // Log share per item if they have IDs
      for (const item of sharePublishedItems) {
        if (item.id) {
          await api.post(`/projects/${projectId}/irs/${item.id}/share`, {
            recipients: sharePubRecipients.map(r => ({ name: r.name, email: r.email, role: r.role })),
            send_type: sharePubSendType,
          }).catch(() => {});
        }
      }
      if (sharePubSendEmail) {
        const toEmails = sharePubRecipients.map(r => r.email).join(',');
        const subject = encodeURIComponent(
          sharePublishedItems.length === 1
            ? `IRS - ${sharePublishedItems[0].description || sharePublishedItems[0].information_required || 'Item'}`
            : `IRS - ${sharePublishedItems.length} Items`
        );
        const body = encodeURIComponent(
          `Dear all,\n\nPlease find the Information Required Schedule details below:\n\n` +
          `Items (${sharePublishedItems.length}):\n${itemSummaries}\n\n` +
          `View online: ${window.location.href}\n\n` +
          `Kind regards`
        );
        window.open(`mailto:${toEmails}?subject=${subject}&body=${body}`, '_blank');
      }
      toast.success(`Shared ${sharePublishedItems.length} item${sharePublishedItems.length !== 1 ? 's' : ''} with ${sharePubRecipients.length} recipients`);
      setSharePublishedOpen(false);
    } catch {
      toast.error('Failed to share items');
    } finally { setSharePubInProgress(false); }
  };

  // Schedule item stats
  const scheduleStats = scheduleItems.reduce((acc, item) => {
    const s = item.status || 'pending';
    acc[s] = (acc[s] || 0) + 1;
    acc.total = (acc.total || 0) + 1;
    // Check if late
    const dueDate = item.information_required_by || item.date_response_required;
    if (dueDate && new Date(dueDate) < new Date() && s !== 'received') {
      acc.late = (acc.late || 0) + 1;
    }
    return acc;
  }, { total: 0, pending: 0, requested: 0, received: 0, late: 0 });



  // Export IRS data
  const handleExport = async (format = 'xlsx') => {
    try {
      const token = localStorage.getItem('token');
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/irs/export?format=${format}`;
      
      // Add current filters if viewing items
      if (selectedProgrammeId) {
        url += `&programme_upload_id=${selectedProgrammeId}`;
      }
      if (statusFilter) {
        url += `&status=${statusFilter}`;
      }
      if (disciplineFilter) {
        url += `&discipline=${disciplineFilter}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `IRS_Schedule_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      a.remove();
      toast.success(`Export downloaded (${format.toUpperCase()})`);
    } catch (error) {
      toast.error('Failed to export schedule');
    }
  };

  // Item detail
  const openDetail = async (item) => {
    setLoadingDetail(true);
    setDetailDialogOpen(true);
    setIsEditing(false);
    setDetailTab('details');
    setChangeReason('');
    try {
      const res = await api.get(`/irs/${item.id}`);
      setSelectedItem(res.data);
      setEditData(res.data);
    } catch (error) {
      toast.error('Failed to load item details');
    } finally {
      setLoadingDetail(false);
    }
  };

  // Start editing
  const startEditing = () => {
    setEditData({ ...selectedItem });
    setIsEditing(true);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditData({ ...selectedItem });
    setIsEditing(false);
    setChangeReason('');
  };

  // Save edited item
  const handleSaveEdit = async () => {
    if (!selectedItem) return;
    setSavingEdit(true);
    try {
      const updatePayload = {
        description: editData.description,
        discipline: editData.discipline,
        consultant: editData.consultant,
        information_category: editData.information_category,
        area: editData.area,
        level: editData.level,
        zone: editData.zone,
        section: editData.section,
        pour: editData.pour,
        location_reference: editData.location_reference,
        work_package: editData.work_package,
        date_information_required: editData.date_information_required,
        required_on_site_date: editData.required_on_site_date,
        target_request_date: editData.target_request_date,
        actual_request_date: editData.actual_request_date,
        actual_received_date: editData.actual_received_date,
        drawing_number: editData.drawing_number,
        drawing_title: editData.drawing_title,
        lead_time_days: editData.lead_time_days,
        comments: editData.comments,
        status: editData.status,
        critical_flag: editData.critical_flag,
        change_reason: changeReason || 'Manual update'
      };
      
      await api.put(`/irs/${selectedItem.id}`, updatePayload);
      toast.success('Item updated successfully');
      
      // Reload the item
      const res = await api.get(`/irs/${selectedItem.id}`);
      setSelectedItem(res.data);
      setEditData(res.data);
      setIsEditing(false);
      setChangeReason('');
      loadData();
    } catch (error) {
      toast.error('Failed to update item');
    } finally {
      setSavingEdit(false);
    }
  };

  // Update edit field
  const updateEditField = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  // Handle creating delay event from IRS item
  const handleCreateDelayEvent = async (itemId) => {
    try {
      const res = await api.post(`/irs/${itemId}/create-delay-event`);
      
      if (res.data.already_exists) {
        toast.info('Delay event already linked to this item');
      } else {
        toast.success(`Delay Event ${res.data.event_number} created`);
      }
      
      // Refresh item details
      if (selectedItem?.id === itemId) {
        const updatedRes = await api.get(`/irs/${itemId}`);
        setSelectedItem(updatedRes.data);
      }
      loadData();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Item is not marked as late');
      } else {
        toast.error('Failed to create delay event');
      }
    }
  };

  // Handle linking to drawing
  const handleLinkDrawing = async (itemId) => {
    setLinkingDrawingItemId(itemId);
    setDrawingSearchOpen(true);
    loadDrawingsForLinking();
  };

  // Load drawings for linking
  const loadDrawingsForLinking = async (searchTerm = '') => {
    setLoadingDrawings(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      params.append('limit', '50');
      
      const res = await api.get(`/projects/${projectId}/drawings-for-linking?${params}`);
      setAvailableDrawings(res.data || []);
    } catch (error) {
      toast.error('Failed to load drawings');
    } finally {
      setLoadingDrawings(false);
    }
  };

  // Select and link a drawing
  const handleSelectDrawing = async (drawing) => {
    if (!linkingDrawingItemId) return;
    
    try {
      await api.post(`/irs/${linkingDrawingItemId}/link-drawing`, {
        drawing_id: drawing.id
      });
      
      toast.success(`Drawing ${drawing.drawing_number || drawing.number} linked`);
      setDrawingSearchOpen(false);
      setLinkingDrawingItemId(null);
      
      // Refresh item details
      if (selectedItem?.id === linkingDrawingItemId) {
        const updatedRes = await api.get(`/irs/${linkingDrawingItemId}`);
        setSelectedItem(updatedRes.data);
      }
      loadData();
    } catch (error) {
      toast.error('Failed to link drawing');
    }
  };

  // Actions
  const handleRequest = async (itemId) => {
    try {
      await api.post(`/irs/${itemId}/request`);
      toast.success('Marked as requested');
      loadData();
      if (selectedItem?.id === itemId) {
        const res = await api.get(`/irs/${itemId}`);
        setSelectedItem(res.data);
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleReceive = async (itemId) => {
    try {
      await api.post(`/irs/${itemId}/receive`);
      toast.success('Marked as received');
      loadData();
      if (selectedItem?.id === itemId) {
        const res = await api.get(`/irs/${itemId}`);
        setSelectedItem(res.data);
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleClose = async (itemId) => {
    try {
      await api.post(`/irs/${itemId}/close`);
      toast.success('Item closed');
      loadData();
      if (selectedItem?.id === itemId) {
        const res = await api.get(`/irs/${itemId}`);
        setSelectedItem(res.data);
      }
    } catch (error) {
      toast.error('Failed to close item');
    }
  };

  const handleCreateRFI = async (itemId) => {
    try {
      const res = await api.post(`/irs/${itemId}/create-rfi`);
      toast.success('RFI created successfully - navigating to RFI');
      loadData();
      if (res.data?.rfi_id) {
        navigate(`/projects/${projectId}/rfis/${res.data.rfi_id}`);
      }
    } catch (error) {
      toast.error('Failed to create RFI');
    }
  };

  // Create new item
  const handleCreateItem = async () => {
    try {
      await api.post(`/projects/${projectId}/irs`, newItem);
      toast.success('IRS item created');
      setCreateDialogOpen(false);
      setNewItem({
        description: '',
        discipline: 'general',
        information_category: 'general',
        area: '',
        level: '',
        zone: '',
        date_information_required: '',
        consultant: '',
        comments: ''
      });
      loadData();
    } catch (error) {
      toast.error('Failed to create item');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6 min-w-0 overflow-x-hidden">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(`/projects/${projectId}`)}
            className="text-slate-600 hover:text-slate-900 mb-3 -ml-2 text-sm"
            data-testid="back-to-project-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Project
          </Button>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-barlow font-bold uppercase tracking-wider text-slate-900">
                Info Required Schedule
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">
                {total} information requirements — Select a programme and manage IRS items
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <WalkthroughTrigger moduleId="irs" />
              {programmeUploads.length >= 2 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCompareDialogOpen(true)}
                  className="rounded-sm"
                  data-testid="compare-programmes-btn"
                >
                  <GitCompare className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Compare</span>
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => { loadLeadTimeConfig(); setLeadTimeConfigOpen(true); }}
                className="rounded-sm"
                data-testid="lead-time-settings-btn"
              >
                <Settings className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">Lead Times</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('xlsx')}
                className="rounded-sm hidden sm:flex"
                data-testid="export-xlsx-btn"
              >
                <Download className="h-4 w-4 sm:mr-1" />
                <span className="hidden md:inline">Export</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={loadData}
                disabled={loading}
                className="rounded-sm"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                size="sm"
                onClick={() => setCreateDialogOpen(true)}
                className="rounded-sm bg-slate-900 hover:bg-slate-800"
                data-testid="create-irs-item-btn"
              >
                <Plus className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">Add Item</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Background Job Completed Banner */}
        {pendingBgJobs.length > 0 && (
          <div className="space-y-2" data-testid="bg-jobs-banner">
            {pendingBgJobs.map(job => (
              <div key={job.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 sm:px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-1.5 bg-emerald-100 rounded-lg flex-shrink-0">
                    <Wand2 className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-emerald-900">AI Draft Ready</p>
                    <p className="text-xs text-emerald-700 truncate">
                      "{job.params?.command}"
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-9 sm:ml-0">
                  <Button
                    size="sm"
                    onClick={() => handleReviewBgJob(job.id)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                    data-testid={`review-bg-job-${job.id}`}
                  >
                    <Eye className="h-3.5 w-3.5 mr-1" /> Review
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPendingBgJobs(prev => prev.filter(j => j.id !== job.id))}
                    className="text-emerald-600 hover:text-emerald-800"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-4">
            <Card className="border-slate-200">
              <CardContent className="p-3 sm:pt-4">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-slate-900">{stats.total || 0}</p>
                  <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wider text-slate-500">Total</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="p-3 sm:pt-4">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-blue-600">{stats.by_status?.requested || 0}</p>
                  <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wider text-blue-600">Requested</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-green-200 bg-green-50/50">
              <CardContent className="p-3 sm:pt-4">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-green-600">{stats.received || 0}</p>
                  <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wider text-green-600">Received</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-orange-200 bg-orange-50/50">
              <CardContent className="p-3 sm:pt-4">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-orange-600">{stats.outstanding || 0}</p>
                  <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wider text-orange-600">Outstanding</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-red-200 bg-red-50/50 hidden sm:block">
              <CardContent className="p-3 sm:pt-4">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-red-600">{stats.late || 0}</p>
                  <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wider text-red-600">Late</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50/50 hidden lg:block">
              <CardContent className="p-3 sm:pt-4">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-amber-600">{stats.critical || 0}</p>
                  <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wider text-amber-600">Critical</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-purple-200 bg-purple-50/50 hidden lg:block">
              <CardContent className="p-3 sm:pt-4">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-purple-600">{stats.upcoming_14_days || 0}</p>
                  <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wider text-purple-600">Due 14d</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Schedule Management Section */}
        <Card className="border-indigo-200 bg-indigo-50/30">
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-indigo-600" />
                <h3 className="font-bold text-sm sm:text-base text-slate-800">
                  {irsActiveView === 'schedule-detail' ? (selectedIrsSchedule?.schedule_name || 'Schedule') : 'IRS Schedules'}
                </h3>
                {selectedIrsSchedule && irsActiveView === 'schedule-detail' && (
                  <Badge variant={selectedIrsSchedule.status === 'published' ? 'default' : 'secondary'} className="text-xs">
                    {selectedIrsSchedule.status}
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {irsActiveView === 'schedule-detail' && (
                  <>
                    <Button variant="outline" size="sm" className="text-xs" onClick={() => { setIrsActiveView('schedules'); setSelectedIrsSchedule(null); setScheduleItems([]); }}>
                      <ArrowLeft className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">All Schedules</span>
                    </Button>
                    {selectedIrsSchedule?.status !== 'published' ? (
                      <Button variant="outline" size="sm" className="text-xs border-green-300 text-green-700 hover:bg-green-50" onClick={handlePublishSchedule} data-testid="publish-schedule-btn">
                        <CheckCircle2 className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Publish</span>
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" className="text-xs border-blue-300 text-blue-700 hover:bg-blue-50" onClick={handlePublishSchedule} data-testid="resync-schedule-btn">
                        <RefreshCw className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Sync</span>
                      </Button>
                    )}
                    <Button variant="outline" size="sm" className="text-xs hidden sm:flex" onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/schedule/${selectedIrsSchedule.id}/export?format=xlsx`, {
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (!res.ok) throw new Error('Export failed');
                        const blob = await res.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a'); a.href = url;
                        a.download = `${selectedIrsSchedule.schedule_name || 'IRS'}_${new Date().toISOString().split('T')[0]}.xlsx`;
                        document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                        toast.success('Excel downloaded');
                      } catch { toast.error('Export failed'); }
                    }} data-testid="export-schedule-xlsx">
                      <Download className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Excel</span>
                    </Button>
                    <Button variant="outline" size="sm" className="text-xs hidden sm:flex" onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/schedule/${selectedIrsSchedule.id}/export?format=pdf`, {
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (!res.ok) throw new Error('Export failed');
                        const blob = await res.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a'); a.href = url;
                        a.download = `${selectedIrsSchedule.schedule_name || 'IRS'}_${new Date().toISOString().split('T')[0]}.pdf`;
                        document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                        toast.success('PDF downloaded');
                      } catch { toast.error('Export failed'); }
                    }} data-testid="export-schedule-pdf">
                      <FileSpreadsheet className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">PDF</span>
                    </Button>
                    <Button variant="outline" size="sm" className="text-xs hidden sm:flex" onClick={() => {
                      setShareDialogOpen(true);
                      loadProjectTeamForShare();
                    }} data-testid="share-schedule-btn">
                      <Send className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Share</span>
                    </Button>
                  </>
                )}
                <Button
                  onClick={() => setAiCommandModalOpen(true)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-xs sm:text-sm"
                  size="sm"
                  data-testid="ai-command-btn"
                >
                  <Wand2 className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Generate New</span>
                  <span className="sm:hidden">AI</span>
                </Button>
              </div>
            </div>
            
            {irsActiveView === 'schedules' && (
              <div className="space-y-3">
                {/* Programme tip */}
                {programmeUploads.length === 0 && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm">
                    <p className="text-sm text-amber-700">
                      <AlertTriangle className="h-4 w-4 inline mr-1" />
                      No programmes uploaded yet. Upload a programme in the <strong>Programs</strong> module first.
                    </p>
                  </div>
                )}
                {programmeUploads.length > 0 && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm" data-testid="data-file-recommendation-irs">
                    <p className="text-sm text-blue-700">
                      <Info className="h-4 w-4 inline mr-1" />
                      Click <strong>Generate New</strong> to create an IRS schedule from the programme.
                    </p>
                  </div>
                )}
                {/* Saved Schedules List */}
                <ScheduleRegister
                  projectId={projectId}
                  scheduleType="irs"
                  programmes={programmeUploads}
                  onOpenSchedule={openSchedule}
                />
              </div>
            )}

            {irsActiveView === 'schedule-detail' && selectedIrsSchedule && (
              <div className="space-y-4">
                {/* Schedule Info Header */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Card><CardContent className="p-2 sm:p-3">
                    <p className="text-[10px] text-slate-500 uppercase">Trade</p>
                    <p className="text-sm font-medium truncate">{selectedIrsSchedule.trade_package || selectedIrsSchedule.schedule_name}</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-2 sm:p-3">
                    <p className="text-[10px] text-slate-500 uppercase">Items</p>
                    <p className="text-sm font-medium">{scheduleItems.length}</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-2 sm:p-3">
                    <p className="text-[10px] text-slate-500 uppercase">Received</p>
                    <p className="text-sm font-medium text-green-600">{scheduleStats.received || 0} / {scheduleStats.total}</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-2 sm:p-3">
                    <p className="text-[10px] text-slate-500 uppercase">Late</p>
                    <p className="text-sm font-medium text-red-600">{scheduleStats.late || 0}</p>
                  </CardContent></Card>
                </div>

                {/* Schedule Items Table */}
                <div className="border rounded-lg overflow-x-auto bg-white">
                  <table className="w-full text-sm min-w-[900px]" data-testid="schedule-items-table">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-bold text-slate-500 uppercase w-10">#</th>
                        <th className="px-3 py-2 text-left text-xs font-bold text-slate-500 uppercase">Information Required</th>
                        <th className="px-3 py-2 text-left text-xs font-bold text-slate-500 uppercase w-24">Area</th>
                        <th className="px-3 py-2 text-left text-xs font-bold text-slate-500 uppercase w-24">Level</th>
                        <th className="px-3 py-2 text-left text-xs font-bold text-slate-500 uppercase">Linked Activity</th>
                        <th className="px-3 py-2 text-left text-xs font-bold text-slate-500 uppercase w-28">On Site Date</th>
                        <th className="px-3 py-2 text-left text-xs font-bold text-slate-500 uppercase w-28">Required By</th>
                        <th className="px-3 py-2 text-left text-xs font-bold text-slate-500 uppercase w-28">Responsible</th>
                        <th className="px-3 py-2 text-center text-xs font-bold text-slate-500 uppercase w-24">Status</th>
                        <th className="px-3 py-2 text-left text-xs font-bold text-slate-500 uppercase w-28">Received</th>
                        <th className="px-3 py-2 text-center text-xs font-bold text-slate-500 uppercase w-20">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {scheduleItems.length === 0 ? (
                        <tr>
                          <td colSpan={11} className="px-4 py-8 text-center text-slate-400 text-sm">
                            No items in this schedule
                          </td>
                        </tr>
                      ) : (
                        scheduleItems.map((item, idx) => {
                          const dueDate = item.information_required_by || item.date_response_required;
                          const isLate = dueDate && new Date(dueDate) < new Date() && item.status !== 'received';
                          return (
                            <tr key={item.id || idx} className={`hover:bg-slate-50 ${isLate ? 'bg-red-50/40' : ''}`} data-testid={`schedule-item-${item.id || idx}`}>
                              <td className="px-3 py-2 text-xs text-slate-400">{item.item_no || idx + 1}</td>
                              <td className="px-3 py-2">
                                <p className="text-sm font-medium text-slate-800 line-clamp-2">{item.description || item.information_required || '-'}</p>
                                {item.trade_package && <p className="text-[10px] text-slate-400 mt-0.5">{item.trade_package}</p>}
                              </td>
                              <td className="px-3 py-2 text-xs text-slate-600">{item.area || '-'}</td>
                              <td className="px-3 py-2 text-xs text-slate-600">{item.level || '-'}</td>
                              <td className="px-3 py-2">
                                <p className="text-xs text-slate-600 line-clamp-1">{item.linked_activity || item.activity_name || '-'}</p>
                              </td>
                              <td className="px-3 py-2 text-xs text-slate-600">
                                {item.required_on_site_date || item.site_required_date || item.activity_start_date || '-'}
                              </td>
                              <td className="px-3 py-2">
                                <span className={`text-xs font-medium ${isLate ? 'text-red-600' : 'text-slate-600'}`}>
                                  {dueDate || '-'}
                                </span>
                                {item.manual_override_date && (
                                  <p className="text-[10px] text-amber-600 mt-0.5">Override: {item.manual_override_date}</p>
                                )}
                              </td>
                              <td className="px-3 py-2 text-xs text-slate-600">{item.responsible_party || item.consultant_responsible || '-'}</td>
                              <td className="px-3 py-2 text-center">
                                <Badge className={`text-[10px] ${STATUS_COLORS[item.status] || STATUS_COLORS.not_requested}`}>
                                  {(item.status || 'pending').replace(/_/g, ' ')}
                                </Badge>
                              </td>
                              <td className="px-3 py-2 text-xs text-slate-600">{item.actual_received_date || '-'}</td>
                              <td className="px-3 py-2 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  {item.status !== 'received' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0 text-green-600 hover:text-green-800"
                                      title="Mark Received"
                                      onClick={() => { setMarkReceivedItem(item); setMarkReceivedOpen(true); }}
                                    >
                                      <CheckCircle2 className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 text-slate-400 hover:text-slate-600"
                                    title="Edit"
                                    onClick={() => { setSelectedItem(item); setDetailDialogOpen(true); }}
                                  >
                                    <Edit2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <AICommandModal
          open={aiCommandModalOpen}
          onClose={() => { setAiCommandModalOpen(false); setBgJobResult(null); }}
          projectId={projectId}
          programmes={programmeUploads}
          defaultType="irs"
          onScheduleSaved={() => { loadItems(); setIrsActiveView('schedules'); }}
          backgroundJobResult={bgJobResult}
        />

        {/* Filters */}
        <Card className="border-slate-200">
          <CardContent className="p-3 sm:pt-4">
            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 sm:gap-3">
              <div className="flex-1 min-w-0 sm:min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search items..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 rounded-sm"
                    data-testid="search-input"
                  />
                </div>
              </div>

              <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[140px] rounded-sm" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="not_requested">Not Requested</SelectItem>
                  <SelectItem value="requested">Requested</SelectItem>
                  <SelectItem value="received">Received</SelectItem>
                  <SelectItem value="late">Late</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>

              <Select value={disciplineFilter || "all"} onValueChange={(v) => setDisciplineFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[140px] rounded-sm" data-testid="discipline-filter">
                  <SelectValue placeholder="Discipline" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Disciplines</SelectItem>
                  <SelectItem value="structural">Structural</SelectItem>
                  <SelectItem value="architectural">Architectural</SelectItem>
                  <SelectItem value="electrical">Electrical</SelectItem>
                  <SelectItem value="mechanical">Mechanical</SelectItem>
                  <SelectItem value="plumbing">Plumbing</SelectItem>
                  <SelectItem value="fire">Fire</SelectItem>
                  <SelectItem value="civil">Civil</SelectItem>
                  <SelectItem value="coordination">Coordination</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant={showCritical ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowCritical(!showCritical)}
                  className="rounded-sm"
                  data-testid="critical-filter"
                >
                  <AlertTriangle className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Critical</span>
                </Button>

                <Button
                  variant={showLate ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowLate(!showLate)}
                  className="rounded-sm"
                  data-testid="late-filter"
                >
                  <Clock className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Late</span>
                </Button>

                {(search || statusFilter || disciplineFilter || areaFilter || showCritical || showLate) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSearch('');
                      setStatusFilter('');
                      setDisciplineFilter('');
                      setAreaFilter('');
                      setShowCritical(false);
                      setShowLate(false);
                    }}
                    className="rounded-sm"
                  >
                    Clear
                  </Button>
                )}

                {/* Share All button for published items */}
                {items.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs rounded-sm border-blue-300 text-blue-700 hover:bg-blue-50"
                    onClick={() => openSharePublished(items)}
                    data-testid="share-all-irs-btn"
                  >
                    <Send className="h-3.5 w-3.5 mr-1" />
                    Share All
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table / Mobile Cards */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-12">
                <ClipboardList className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No IRS items found</p>
                <p className="text-sm text-slate-400 mt-1">
                  {programmeUploads.length === 0
                    ? 'Upload a programme in the Programs module first'
                    : 'Select a programme or create a new item'}
                </p>
              </div>
            ) : (
              <>
                {/* Mobile Card View - Grouped by Programme -> Trade */}
                <div className="sm:hidden" data-testid="irs-grouped-mobile">
                  {Object.entries(groupedItems).map(([progId, progGroup]) => (
                    <div key={progId} className="border-b-2 border-slate-200 last:border-b-0" data-testid={`programme-group-${progId}`}>
                      <button
                        className="w-full flex items-center justify-between p-3 bg-blue-50/60 hover:bg-blue-100/60 transition-colors text-left"
                        onClick={() => toggleProgramme(progId)}
                        data-testid={`toggle-programme-${progId}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <ChevronRight className={`h-4 w-4 text-blue-600 transition-transform duration-200 flex-shrink-0 ${expandedProgrammes[progId] ? 'rotate-90' : ''}`} />
                          <FolderOpen className="h-4 w-4 text-blue-600 flex-shrink-0" />
                          <span className="font-bold text-sm text-slate-800 truncate">{progGroup.name}</span>
                        </div>
                        <Badge className="bg-blue-100 text-blue-700 text-xs flex-shrink-0 ml-2">{progGroup.count} items</Badge>
                      </button>
                      {expandedProgrammes[progId] && (
                        <div className="pl-2">
                          {Object.entries(progGroup.trades).map(([trade, tradeItems]) => {
                            const tradeKey = `${progId}__${trade}`;
                            return (
                              <div key={trade} className="border-b border-slate-100 last:border-b-0">
                                <button
                                  className="w-full flex items-center justify-between p-2 pl-4 bg-amber-50/40 hover:bg-amber-100/40 transition-colors text-left"
                                  onClick={() => toggleTrade(progId, trade)}
                                  data-testid={`toggle-trade-${tradeKey}`}
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    <ChevronRight className={`h-3.5 w-3.5 text-amber-600 transition-transform duration-200 flex-shrink-0 ${expandedTrades[tradeKey] ? 'rotate-90' : ''}`} />
                                    <Building className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" />
                                    <span className="font-medium text-sm text-slate-700 truncate">{trade}</span>
                                  </div>
                                  <Badge className="bg-amber-100 text-amber-700 text-xs flex-shrink-0 ml-2">{tradeItems.length}</Badge>
                                </button>
                                {expandedTrades[tradeKey] && (
                                  <div className="divide-y divide-slate-100">
                                    {tradeItems.map((item) => (
                                      <div
                                        key={item.id}
                                        className="p-3 pl-10 cursor-pointer hover:bg-slate-50"
                                        onClick={() => openDetail(item)}
                                        data-testid={`irs-item-mobile-${item.id}`}
                                      >
                                        <div className="flex items-start justify-between mb-1">
                                          <p className="font-medium text-slate-900 text-sm truncate flex-1 min-w-0">
                                            {item.description || 'No description'}
                                          </p>
                                          <Badge className={`rounded-sm text-xs ml-2 flex-shrink-0 ${STATUS_COLORS[item.status] || ''}`}>
                                            {item.status?.replace('_', ' ')}
                                          </Badge>
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <Badge className={`rounded-sm text-xs ${DISCIPLINE_COLORS[item.discipline] || ''}`}>
                                            {item.discipline?.replace('_', ' ')}
                                          </Badge>
                                          {item.critical_flag && <Badge className="rounded-sm text-xs bg-amber-100 text-amber-700">Critical</Badge>}
                                          {item.delay_flag && <Badge className="rounded-sm text-xs bg-red-100 text-red-700">Late</Badge>}
                                        </div>
                                        <div className="flex items-center justify-between mt-1 text-xs text-slate-500">
                                          <span>Required: {formatDate(item.date_information_required)}</span>
                                          {item.consultant && <span>{item.consultant}</span>}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Desktop Table View - Grouped by Programme -> Trade */}
                <div className="hidden sm:block overflow-x-auto" data-testid="irs-grouped-desktop">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="font-bold min-w-[250px]">Information Required</TableHead>
                        <TableHead className="font-bold min-w-[100px]">Discipline</TableHead>
                        <TableHead className="font-bold min-w-[120px]">Consultant</TableHead>
                        <TableHead className="font-bold min-w-[100px] text-center">Required By</TableHead>
                        <TableHead className="font-bold min-w-[100px] text-center">Requested</TableHead>
                        <TableHead className="font-bold min-w-[100px] text-center">Received</TableHead>
                        <TableHead className="font-bold min-w-[90px] text-center">Status</TableHead>
                        <TableHead className="font-bold min-w-[60px] text-center">Flags</TableHead>
                        <TableHead className="min-w-[60px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(groupedItems).map(([progId, progGroup]) => (
                        <React.Fragment key={progId}>
                          <TableRow
                            className="bg-blue-50/60 cursor-pointer hover:bg-blue-100/60 border-t-2 border-blue-200"
                            onClick={() => toggleProgramme(progId)}
                            data-testid={`programme-group-desktop-${progId}`}
                          >
                            <TableCell colSpan={9} className="py-2.5">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <ChevronRight className={`h-4 w-4 text-blue-600 transition-transform duration-200 ${expandedProgrammes[progId] ? 'rotate-90' : ''}`} />
                                  <FolderOpen className="h-4 w-4 text-blue-600" />
                                  <span className="font-bold text-sm text-slate-800">{progGroup.name}</span>
                                </div>
                                <Badge className="bg-blue-100 text-blue-700 text-xs">{progGroup.count} items</Badge>
                              </div>
                            </TableCell>
                          </TableRow>
                          {expandedProgrammes[progId] && Object.entries(progGroup.trades).map(([trade, tradeItems]) => {
                            const tradeKey = `${progId}__${trade}`;
                            return (
                              <React.Fragment key={trade}>
                                <TableRow
                                  className="bg-amber-50/40 cursor-pointer hover:bg-amber-100/40"
                                  onClick={() => toggleTrade(progId, trade)}
                                  data-testid={`trade-group-desktop-${tradeKey}`}
                                >
                                  <TableCell colSpan={9} className="py-2 pl-10">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <ChevronRight className={`h-3.5 w-3.5 text-amber-600 transition-transform duration-200 ${expandedTrades[tradeKey] ? 'rotate-90' : ''}`} />
                                        <Building className="h-3.5 w-3.5 text-amber-600" />
                                        <span className="font-medium text-sm text-slate-700">{trade}</span>
                                      </div>
                                      <Badge className="bg-amber-100 text-amber-700 text-xs">{tradeItems.length}</Badge>
                                    </div>
                                  </TableCell>
                                </TableRow>
                                {expandedTrades[tradeKey] && tradeItems.map((item) => (
                                  <TableRow
                                    key={item.id}
                                    className={`cursor-pointer hover:bg-slate-50 ${item.delay_flag ? 'bg-red-50/30' : ''}`}
                                    onClick={() => openDetail(item)}
                                    data-testid={`irs-item-${item.id}`}
                                  >
                                    <TableCell className="pl-16">
                                      <div>
                                        <p className="font-medium text-slate-900 text-sm">
                                          {item.description?.substring(0, 60) || 'No description'}
                                          {item.description?.length > 60 && '...'}
                                        </p>
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <Badge className={`rounded-sm text-xs ${DISCIPLINE_COLORS[item.discipline] || ''}`}>
                                        {item.discipline?.replace('_', ' ')}
                                      </Badge>
                                    </TableCell>
                                    <TableCell>
                                      <span className="text-sm">{item.consultant || '-'}</span>
                                    </TableCell>
                                    <TableCell className="text-center text-sm">
                                      {formatDate(item.date_information_required)}
                                    </TableCell>
                                    <TableCell className="text-center text-sm">
                                      {formatDate(item.actual_request_date)}
                                    </TableCell>
                                    <TableCell className="text-center text-sm">
                                      {formatDate(item.actual_received_date)}
                                    </TableCell>
                                    <TableCell className="text-center">
                                      <Badge className={`rounded-sm text-xs ${STATUS_COLORS[item.status] || ''}`}>
                                        {item.status?.replace('_', ' ')}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="text-center">
                                      <div className="flex items-center justify-center gap-1">
                                        {item.critical_flag && (
                                          <AlertTriangle className="h-4 w-4 text-amber-500" title="Critical" />
                                        )}
                                        {item.delay_flag && (
                                          <Clock className="h-4 w-4 text-red-500" title="Late" />
                                        )}
                                        {item.linked_rfi_id && (
                                          <FileText className="h-4 w-4 text-blue-500" title="RFI Linked" />
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <ChevronRight className="h-4 w-4 text-slate-400" />
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>

      {/* Detail Dialog - Enhanced with Editing */}
      <Dialog open={detailDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setIsEditing(false);
          setChangeReason('');
        }
        setDetailDialogOpen(open);
      }}>
        <DialogContent className="sm:max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col p-4 sm:p-6">
          {loadingDetail ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : selectedItem && (
            <>
              {/* Header */}
              <DialogHeader className="flex-shrink-0">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex-1">
                    <DialogTitle className="text-base sm:text-lg font-barlow font-bold uppercase leading-tight" data-testid="irs-detail-title">
                      {isEditing ? (
                        <Input
                          value={editData.description || ''}
                          onChange={(e) => updateEditField('description', e.target.value)}
                          className="text-sm sm:text-lg font-bold"
                          data-testid="edit-description-input"
                        />
                      ) : (
                        <span className="line-clamp-2">{selectedItem.description}</span>
                      )}
                    </DialogTitle>
                    <div className="flex items-center gap-1.5 sm:gap-2 mt-2 flex-wrap">
                      <Badge className={`rounded-sm text-xs ${DISCIPLINE_COLORS[selectedItem.discipline] || ''}`}>
                        {selectedItem.discipline?.replace('_', ' ')}
                      </Badge>
                      <Badge className={`rounded-sm text-xs ${STATUS_COLORS[selectedItem.status] || ''}`}>
                        {selectedItem.status?.replace('_', ' ')}
                      </Badge>
                      {selectedItem.source === 'ai_generated' && (
                        <Badge className="rounded-sm bg-purple-100 text-purple-700 text-xs hidden sm:flex">
                          <Sparkles className="h-3 w-3 mr-1" />
                          AI
                        </Badge>
                      )}
                      {selectedItem.critical_flag && (
                        <Badge className="rounded-sm bg-amber-100 text-amber-700 text-xs">
                          <AlertTriangle className="h-3 w-3 sm:mr-1" />
                          <span className="hidden sm:inline">Critical</span>
                        </Badge>
                      )}
                      {selectedItem.delay_flag && (
                        <Badge className="rounded-sm bg-red-100 text-red-700 text-xs">
                          <Clock className="h-3 w-3 sm:mr-1" />
                          <span className="hidden sm:inline">{selectedItem.days_late ? `${selectedItem.days_late}d late` : 'Late'}</span>
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {!isEditing ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={startEditing}
                        className="rounded-sm flex-1 sm:flex-none"
                        data-testid="edit-irs-btn"
                      >
                        <Pencil className="h-4 w-4 sm:mr-1" />
                        <span className="sm:inline">Edit</span>
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={cancelEditing}
                          className="rounded-sm"
                          data-testid="cancel-edit-btn"
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSaveEdit}
                          disabled={savingEdit}
                          className="rounded-sm bg-emerald-600 hover:bg-emerald-700"
                          data-testid="save-edit-btn"
                        >
                          {savingEdit ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Save className="h-4 w-4 sm:mr-1" />
                              <span className="hidden sm:inline">Save</span>
                            </>
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </DialogHeader>

              {/* Tabs */}
              <Tabs value={detailTab} onValueChange={setDetailTab} className="flex-1 flex flex-col overflow-hidden mt-2">
                <TabsList className="grid w-full grid-cols-4 flex-shrink-0">
                  <TabsTrigger value="details" className="text-xs px-1 sm:px-3">
                    <Eye className="h-3 w-3 sm:mr-1" />
                    <span className="hidden sm:inline">Details</span>
                  </TabsTrigger>
                  <TabsTrigger value="dates" className="text-xs px-1 sm:px-3">
                    <CalendarDays className="h-3 w-3 sm:mr-1" />
                    <span className="hidden sm:inline">Dates</span>
                  </TabsTrigger>
                  <TabsTrigger value="links" className="text-xs px-1 sm:px-3">
                    <Link2 className="h-3 w-3 sm:mr-1" />
                    <span className="hidden sm:inline">Links</span>
                  </TabsTrigger>
                  <TabsTrigger value="history" className="text-xs px-1 sm:px-3">
                    <History className="h-3 w-3 sm:mr-1" />
                    <span className="hidden sm:inline">History</span>
                  </TabsTrigger>
                </TabsList>

                <div className="flex-1 overflow-y-auto mt-2 sm:mt-4">
                  {/* Details Tab */}
                  <TabsContent value="details" className="mt-0 space-y-3 sm:space-y-4">
                    {/* Classification */}
                    <div className="space-y-2 sm:space-y-3">
                      <h4 className="text-xs sm:text-sm font-bold uppercase text-slate-700 flex items-center">
                        <Layers className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                        Classification
                      </h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                        <div className="p-2 sm:p-3 bg-slate-50 rounded-sm">
                          <p className="text-[10px] sm:text-xs text-slate-500 uppercase mb-1">Discipline</p>
                          {isEditing ? (
                            <Select value={editData.discipline || ''} onValueChange={(v) => updateEditField('discipline', v)}>
                              <SelectTrigger className="h-7 sm:h-8 text-xs sm:text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="structural">Structural</SelectItem>
                                <SelectItem value="architectural">Architectural</SelectItem>
                                <SelectItem value="electrical">Electrical</SelectItem>
                                <SelectItem value="mechanical">Mechanical</SelectItem>
                                <SelectItem value="plumbing">Plumbing</SelectItem>
                                <SelectItem value="fire">Fire</SelectItem>
                                <SelectItem value="civil">Civil</SelectItem>
                                <SelectItem value="facade">Facade</SelectItem>
                                <SelectItem value="coordination">Coordination</SelectItem>
                                <SelectItem value="general">General</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <p className="font-medium text-xs sm:text-sm capitalize">{selectedItem.discipline?.replace('_', ' ') || '-'}</p>
                          )}
                        </div>
                        <div className="p-2 sm:p-3 bg-slate-50 rounded-sm">
                          <p className="text-[10px] sm:text-xs text-slate-500 uppercase mb-1">Consultant</p>
                          {isEditing ? (
                            <Input
                              value={editData.consultant || ''}
                              onChange={(e) => updateEditField('consultant', e.target.value)}
                              className="h-7 sm:h-8 text-xs sm:text-sm"
                              placeholder="e.g., Structural Engineer"
                            />
                          ) : (
                            <p className="font-medium text-xs sm:text-sm truncate">{selectedItem.consultant || '-'}</p>
                          )}
                        </div>
                        <div className="p-2 sm:p-3 bg-slate-50 rounded-sm">
                          <p className="text-[10px] sm:text-xs text-slate-500 uppercase mb-1">Work Package</p>
                          {isEditing ? (
                            <Input
                              value={editData.work_package || ''}
                              onChange={(e) => updateEditField('work_package', e.target.value)}
                              className="h-7 sm:h-8 text-xs sm:text-sm"
                            />
                          ) : (
                            <p className="font-medium text-xs sm:text-sm truncate">{selectedItem.work_package || '-'}</p>
                          )}
                        </div>
                        <div className="p-2 sm:p-3 bg-slate-50 rounded-sm">
                          <p className="text-[10px] sm:text-xs text-slate-500 uppercase mb-1">Lead Time</p>
                          {isEditing ? (
                            <Input
                              type="number"
                              value={editData.lead_time_days || 15}
                              onChange={(e) => updateEditField('lead_time_days', parseInt(e.target.value))}
                              className="h-7 sm:h-8 text-xs sm:text-sm"
                            />
                          ) : (
                            <p className="font-medium text-xs sm:text-sm">{selectedItem.lead_time_days || 15} days</p>
                          )}
                        </div>
                        <div className="p-2 sm:p-3 bg-slate-50 rounded-sm">
                          <p className="text-[10px] sm:text-xs text-slate-500 uppercase mb-1">Status</p>
                          {isEditing ? (
                            <Select value={editData.status || ''} onValueChange={(v) => updateEditField('status', v)}>
                              <SelectTrigger className="h-7 sm:h-8 text-xs sm:text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="not_requested">Not Requested</SelectItem>
                                <SelectItem value="requested">Requested</SelectItem>
                                <SelectItem value="received">Received</SelectItem>
                                <SelectItem value="under_review">Under Review</SelectItem>
                                <SelectItem value="approved">Approved</SelectItem>
                                <SelectItem value="late">Late</SelectItem>
                                <SelectItem value="closed">Closed</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge className={`text-xs ${STATUS_COLORS[selectedItem.status] || ''}`}>
                              {selectedItem.status?.replace('_', ' ')}
                            </Badge>
                          )}
                        </div>
                        <div className="p-2 sm:p-3 bg-slate-50 rounded-sm">
                          <p className="text-[10px] sm:text-xs text-slate-500 uppercase mb-1">Critical</p>
                          {isEditing ? (
                            <Select 
                              value={editData.critical_flag ? 'yes' : 'no'} 
                              onValueChange={(v) => updateEditField('critical_flag', v === 'yes')}
                            >
                              <SelectTrigger className="h-7 sm:h-8 text-xs sm:text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="yes">Yes</SelectItem>
                                <SelectItem value="no">No</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <p className="font-medium text-xs sm:text-sm">{selectedItem.critical_flag ? 'Yes' : 'No'}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Location */}
                    <div className="space-y-2 sm:space-y-3">
                      <h4 className="text-xs sm:text-sm font-bold uppercase text-slate-700 flex items-center">
                        <MapPin className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                        Location
                      </h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                        <div className="p-2 sm:p-3 bg-slate-50 rounded-sm">
                          <p className="text-[10px] sm:text-xs text-slate-500 uppercase mb-1">Area/Level</p>
                          {isEditing ? (
                            <Input
                              value={editData.area || editData.level || ''}
                              onChange={(e) => { updateEditField('area', e.target.value); updateEditField('level', e.target.value); }}
                              className="h-7 sm:h-8 text-xs sm:text-sm"
                              placeholder="e.g., Block A / Level 2"
                            />
                          ) : (
                            <p className="font-medium text-xs sm:text-sm">{selectedItem.area || selectedItem.level || selectedItem.wbs || '-'}</p>
                          )}
                        </div>
                        <div className="p-3 bg-slate-50 rounded-sm">
                          <p className="text-xs text-slate-500 uppercase mb-1">Zone</p>
                          {isEditing ? (
                            <Input
                              value={editData.zone || ''}
                              onChange={(e) => updateEditField('zone', e.target.value)}
                              className="h-8 text-sm"
                              placeholder="e.g., Zone A"
                            />
                          ) : (
                            <p className="font-medium">{selectedItem.zone?.replace('_', ' ')?.replace(/\b\w/g, l => l.toUpperCase()) || '-'}</p>
                          )}
                        </div>
                        <div className="p-3 bg-slate-50 rounded-sm">
                          <p className="text-xs text-slate-500 uppercase mb-1">Section</p>
                          {isEditing ? (
                            <Input
                              value={editData.section || ''}
                              onChange={(e) => updateEditField('section', e.target.value)}
                              className="h-8 text-sm"
                            />
                          ) : (
                            <p className="font-medium">{selectedItem.section || '-'}</p>
                          )}
                        </div>
                        <div className="p-3 bg-slate-50 rounded-sm">
                          <p className="text-xs text-slate-500 uppercase mb-1">Pour</p>
                          {isEditing ? (
                            <Input
                              value={editData.pour || ''}
                              onChange={(e) => updateEditField('pour', e.target.value)}
                              className="h-8 text-sm"
                            />
                          ) : (
                            <p className="font-medium">{selectedItem.pour || '-'}</p>
                          )}
                        </div>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-sm">
                        <p className="text-xs text-slate-500 uppercase mb-1">Location Reference</p>
                        {isEditing ? (
                          <Input
                            value={editData.location_reference || ''}
                            onChange={(e) => updateEditField('location_reference', e.target.value)}
                            className="h-8 text-sm"
                          />
                        ) : (
                          <p className="font-medium">{selectedItem.location_reference || '-'}</p>
                        )}
                      </div>
                    </div>

                    <Separator />

                    {/* Drawing Info */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-bold uppercase text-slate-700 flex items-center">
                        <FileText className="h-4 w-4 mr-2" />
                        Drawing Information
                      </h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-slate-50 rounded-sm">
                          <p className="text-xs text-slate-500 uppercase mb-1">Drawing Number</p>
                          {isEditing ? (
                            <Input
                              value={editData.drawing_number || ''}
                              onChange={(e) => updateEditField('drawing_number', e.target.value)}
                              className="h-8 text-sm"
                              placeholder="e.g., STR-001"
                            />
                          ) : (
                            <p className="font-medium">{selectedItem.drawing_number || '-'}</p>
                          )}
                        </div>
                        <div className="p-3 bg-slate-50 rounded-sm">
                          <p className="text-xs text-slate-500 uppercase mb-1">Drawing Title</p>
                          {isEditing ? (
                            <Input
                              value={editData.drawing_title || ''}
                              onChange={(e) => updateEditField('drawing_title', e.target.value)}
                              className="h-8 text-sm"
                            />
                          ) : (
                            <p className="font-medium">{selectedItem.drawing_title || '-'}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Comments */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-bold uppercase text-slate-700">Comments</h4>
                      {isEditing ? (
                        <Textarea
                          value={editData.comments || ''}
                          onChange={(e) => updateEditField('comments', e.target.value)}
                          className="text-sm"
                          placeholder="Add any additional notes..."
                          rows={3}
                        />
                      ) : (
                        <p className="text-sm text-slate-600 p-3 bg-slate-50 rounded-sm min-h-[60px]">
                          {selectedItem.comments || 'No comments'}
                        </p>
                      )}
                    </div>

                    {/* Change Reason (when editing) */}
                    {isEditing && (
                      <div className="space-y-2 pt-2 border-t">
                        <Label className="text-xs text-slate-500">Reason for change (optional)</Label>
                        <Input
                          value={changeReason}
                          onChange={(e) => setChangeReason(e.target.value)}
                          placeholder="e.g., Updated consultant contact"
                          className="text-sm"
                        />
                      </div>
                    )}
                  </TabsContent>

                  {/* Dates Tab */}
                  <TabsContent value="dates" className="mt-0 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      {/* Planned Dates */}
                      <div className="space-y-3">
                        <h4 className="text-sm font-bold uppercase text-slate-700">Planned Dates</h4>
                        <div className="space-y-2">
                          <div className="p-3 bg-amber-50 rounded-sm border border-amber-200">
                            <p className="text-xs text-amber-700 uppercase mb-1">Information Required By</p>
                            {isEditing ? (
                              <Input
                                type="date"
                                value={editData.date_information_required?.split('T')[0] || ''}
                                onChange={(e) => updateEditField('date_information_required', e.target.value)}
                                className="h-8 text-sm"
                              />
                            ) : (
                              <p className="font-bold text-amber-900">{formatDate(selectedItem.date_information_required)}</p>
                            )}
                          </div>
                          <div className="p-3 bg-slate-50 rounded-sm">
                            <p className="text-xs text-slate-500 uppercase mb-1">Required On Site</p>
                            {isEditing ? (
                              <Input
                                type="date"
                                value={editData.required_on_site_date?.split('T')[0] || ''}
                                onChange={(e) => updateEditField('required_on_site_date', e.target.value)}
                                className="h-8 text-sm"
                              />
                            ) : (
                              <p className="font-medium">{formatDate(selectedItem.required_on_site_date)}</p>
                            )}
                          </div>
                          <div className="p-3 bg-slate-50 rounded-sm">
                            <p className="text-xs text-slate-500 uppercase mb-1">Target Request Date</p>
                            {isEditing ? (
                              <Input
                                type="date"
                                value={editData.target_request_date?.split('T')[0] || ''}
                                onChange={(e) => updateEditField('target_request_date', e.target.value)}
                                className="h-8 text-sm"
                              />
                            ) : (
                              <p className="font-medium">{formatDate(selectedItem.target_request_date)}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Actual Dates */}
                      <div className="space-y-3">
                        <h4 className="text-sm font-bold uppercase text-slate-700">Actual Dates</h4>
                        <div className="space-y-2">
                          <div className="p-3 bg-blue-50 rounded-sm border border-blue-200">
                            <p className="text-xs text-blue-700 uppercase mb-1">Actually Requested</p>
                            {isEditing ? (
                              <Input
                                type="date"
                                value={editData.actual_request_date?.split('T')[0] || ''}
                                onChange={(e) => updateEditField('actual_request_date', e.target.value)}
                                className="h-8 text-sm"
                              />
                            ) : (
                              <p className="font-bold text-blue-900">{formatDate(selectedItem.actual_request_date)}</p>
                            )}
                          </div>
                          <div className="p-3 bg-green-50 rounded-sm border border-green-200">
                            <p className="text-xs text-green-700 uppercase mb-1">Actually Received</p>
                            {isEditing ? (
                              <Input
                                type="date"
                                value={editData.actual_received_date?.split('T')[0] || ''}
                                onChange={(e) => updateEditField('actual_received_date', e.target.value)}
                                className="h-8 text-sm"
                              />
                            ) : (
                              <p className="font-bold text-green-900">{formatDate(selectedItem.actual_received_date)}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Days Late Indicator */}
                    {selectedItem.delay_flag && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-sm">
                        <div className="flex items-center gap-2">
                          <AlertOctagon className="h-5 w-5 text-red-600" />
                          <span className="font-bold text-red-700">
                            This item is {selectedItem.days_late || 0} days late
                          </span>
                        </div>
                        <p className="text-sm text-red-600 mt-1">
                          Information was required by {formatDate(selectedItem.date_information_required)} but has not been received.
                        </p>
                      </div>
                    )}
                  </TabsContent>

                  {/* Links Tab */}
                  <TabsContent value="links" className="mt-0 space-y-4">
                    {/* Linked Items */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-bold uppercase text-slate-700">Linked Items</h4>
                      <div className="space-y-2">
                        {selectedItem.linked_rfi_id ? (
                          <div className="p-3 bg-blue-50 rounded-sm flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-blue-600" />
                              <div>
                                <span className="text-sm font-medium">RFI Linked</span>
                                <p className="text-[10px] text-blue-500">Created from IRS item</p>
                              </div>
                            </div>
                            <Button variant="ghost" size="sm" className="text-blue-600"
                              onClick={() => window.location.href = `/projects/${projectId}/rfis`}
                            >
                              <ExternalLink className="h-4 w-4 mr-1" />
                              View RFI
                            </Button>
                          </div>
                        ) : (
                          <div className="p-3 bg-slate-50 rounded-sm flex items-center justify-between">
                            <span className="text-sm text-slate-500">No RFI linked</span>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleCreateRFI(selectedItem.id)}
                              disabled={selectedItem.status === 'closed'}
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Create RFI
                            </Button>
                          </div>
                        )}

                        {selectedItem.linked_drawing_id ? (
                          <div className="p-3 bg-green-50 rounded-sm flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <FileCheck className="h-4 w-4 text-green-600" />
                              <span className="text-sm font-medium">Drawing Linked</span>
                            </div>
                            <Button variant="ghost" size="sm" className="text-green-600">
                              <ExternalLink className="h-4 w-4 mr-1" />
                              View Drawing
                            </Button>
                          </div>
                        ) : (
                          <div className="p-3 bg-slate-50 rounded-sm flex items-center justify-between">
                            <span className="text-sm text-slate-500">No drawing linked</span>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleLinkDrawing(selectedItem.id)}
                            >
                              <Link2 className="h-4 w-4 mr-1" />
                              Link Drawing
                            </Button>
                          </div>
                        )}

                        {selectedItem.linked_delay_event_id ? (
                          <div className="p-3 bg-red-50 rounded-sm flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <AlertTriangle className="h-4 w-4 text-red-600" />
                              <span className="text-sm font-medium">Delay Event Linked</span>
                            </div>
                            <Button variant="ghost" size="sm" className="text-red-600"
                              onClick={() => window.location.href = `/projects/${projectId}/events`}
                            >
                              <ExternalLink className="h-4 w-4 mr-1" />
                              View Delay
                            </Button>
                          </div>
                        ) : (
                          <div className="p-3 bg-slate-50 rounded-sm">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-slate-500">No delay event linked</span>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleCreateDelayEvent(selectedItem.id)}
                                data-testid="create-delay-event-btn"
                              >
                                <AlertOctagon className="h-4 w-4 mr-1" />
                                Create Delay Event
                              </Button>
                            </div>
                            {!selectedItem.delay_flag && (
                              <p className="text-[10px] text-amber-600 mt-1">
                                Note: Item must be marked as late before a delay event can be raised. Change the status to "Late" first.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <Separator />

                    {/* Programme Link */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-bold uppercase text-slate-700">Programme Link</h4>
                      <div className="p-3 bg-slate-50 rounded-sm space-y-2">
                        <div className="flex justify-between">
                          <span className="text-xs text-slate-500">Activity Name</span>
                          <span className="text-sm font-medium">{selectedItem.activity_name || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-xs text-slate-500">WBS</span>
                          <span className="text-sm font-medium">{selectedItem.wbs || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-xs text-slate-500">Linked Activities</span>
                          <span className="text-sm font-medium">{selectedItem.linked_activities_count || selectedItem.activity_ids?.length || 0}</span>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {/* History Tab */}
                  <TabsContent value="history" className="mt-0 space-y-4">
                    <div className="space-y-3">
                      <h4 className="text-sm font-bold uppercase text-slate-700">Revision History</h4>
                      {selectedItem.revision_history && selectedItem.revision_history.length > 0 ? (
                        <div className="space-y-2">
                          {selectedItem.revision_history.slice().reverse().map((entry, idx) => (
                            <div key={idx} className="p-3 bg-slate-50 rounded-sm border-l-4 border-blue-400">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-slate-500">
                                  {formatDate(entry.timestamp)} {entry.timestamp?.split('T')[1]?.substring(0, 5)}
                                </span>
                                {entry.reason && (
                                  <Badge variant="outline" className="text-xs">
                                    {entry.reason}
                                  </Badge>
                                )}
                              </div>
                              <div className="text-sm">
                                {Object.entries(entry.changes || {}).map(([key, value]) => (
                                  key !== 'change_reason' && (
                                    <div key={key} className="flex gap-2">
                                      <span className="text-slate-500 capitalize">{key.replace(/_/g, ' ')}:</span>
                                      <span className="font-medium">{String(value)}</span>
                                    </div>
                                  )
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 p-4 bg-slate-50 rounded-sm text-center">
                          No revision history available
                        </p>
                      )}
                    </div>

                    <Separator />

                    {/* Audit Info */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-bold uppercase text-slate-700">Audit Information</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="p-3 bg-slate-50 rounded-sm">
                          <p className="text-xs text-slate-500">Created</p>
                          <p className="font-medium">{formatDate(selectedItem.created_at)}</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-sm">
                          <p className="text-xs text-slate-500">Last Updated</p>
                          <p className="font-medium">{formatDate(selectedItem.last_updated_at)}</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-sm">
                          <p className="text-xs text-slate-500">Source</p>
                          <p className="font-medium capitalize">{selectedItem.source?.replace('_', ' ') || 'Manual'}</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-sm">
                          <p className="text-xs text-slate-500">Revision</p>
                          <p className="font-medium">Rev {selectedItem.revision_number || 1}</p>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                </div>
              </Tabs>

            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Item Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-barlow font-bold uppercase">
              Create IRS Item
            </DialogTitle>
            <DialogDescription>
              Manually add an information requirement
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Description *</Label>
              <Textarea
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                placeholder="Describe the information required..."
                className="mt-1 rounded-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Discipline</Label>
                <Select
                  value={newItem.discipline}
                  onValueChange={(v) => setNewItem({ ...newItem, discipline: v })}
                >
                  <SelectTrigger className="mt-1 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="structural">Structural</SelectItem>
                    <SelectItem value="architectural">Architectural</SelectItem>
                    <SelectItem value="electrical">Electrical</SelectItem>
                    <SelectItem value="mechanical">Mechanical</SelectItem>
                    <SelectItem value="plumbing">Plumbing</SelectItem>
                    <SelectItem value="fire">Fire</SelectItem>
                    <SelectItem value="civil">Civil</SelectItem>
                    <SelectItem value="coordination">Coordination</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Consultant</Label>
                <Input
                  value={newItem.consultant}
                  onChange={(e) => setNewItem({ ...newItem, consultant: e.target.value })}
                  placeholder="e.g., Structural Engineer"
                  className="mt-1 rounded-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Area/Level</Label>
                <Input
                  value={newItem.level}
                  onChange={(e) => setNewItem({ ...newItem, level: e.target.value })}
                  placeholder="e.g., Ground Floor"
                  className="mt-1 rounded-sm"
                />
              </div>
              <div>
                <Label>Zone</Label>
                <Input
                  value={newItem.zone}
                  onChange={(e) => setNewItem({ ...newItem, zone: e.target.value })}
                  placeholder="e.g., Zone A"
                  className="mt-1 rounded-sm"
                />
              </div>
              <div>
                <Label>Required By</Label>
                <Input
                  type="date"
                  value={newItem.date_information_required}
                  onChange={(e) => setNewItem({ ...newItem, date_information_required: e.target.value })}
                  className="mt-1 rounded-sm"
                />
              </div>
            </div>

            <div>
              <Label>Comments</Label>
              <Textarea
                value={newItem.comments}
                onChange={(e) => setNewItem({ ...newItem, comments: e.target.value })}
                placeholder="Any additional notes..."
                className="mt-1 rounded-sm"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button
              onClick={handleCreateItem}
              disabled={!newItem.description}
              className="rounded-sm bg-slate-900 hover:bg-slate-800"
            >
              <Plus className="h-4 w-4 mr-1" />
              Create Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Drawing Search/Link Dialog */}
      <Dialog open={drawingSearchOpen} onOpenChange={(open) => {
        if (!open) {
          setLinkingDrawingItemId(null);
          setDrawingSearch('');
        }
        setDrawingSearchOpen(open);
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-barlow font-bold uppercase">
              Link Drawing
            </DialogTitle>
            <DialogDescription>
              Search and select a drawing from the Drawing Register to link to this IRS item
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by drawing number or title..."
                value={drawingSearch}
                onChange={(e) => {
                  setDrawingSearch(e.target.value);
                  loadDrawingsForLinking(e.target.value);
                }}
                className="pl-9 rounded-sm"
                data-testid="drawing-search-input"
              />
            </div>

            {/* Drawings List */}
            <div className="max-h-[300px] overflow-y-auto border rounded-sm">
              {loadingDrawings ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : availableDrawings.length === 0 ? (
                <div className="text-center py-8">
                  <FileCheck className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No drawings found</p>
                  <p className="text-xs text-slate-400">Try adjusting your search</p>
                </div>
              ) : (
                <div className="divide-y">
                  {availableDrawings.map((drawing) => (
                    <div
                      key={drawing.id}
                      className="p-3 hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => handleSelectDrawing(drawing)}
                      data-testid={`drawing-option-${drawing.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900 text-sm">
                            {drawing.drawing_number || drawing.number || 'No Number'}
                          </p>
                          <p className="text-xs text-slate-500 truncate max-w-[300px]">
                            {drawing.title || 'No Title'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {drawing.discipline && (
                            <Badge className="rounded-sm text-xs bg-slate-100 text-slate-600">
                              {drawing.discipline}
                            </Badge>
                          )}
                          <ChevronRight className="h-4 w-4 text-slate-400" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDrawingSearchOpen(false)} className="rounded-sm">
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lead Time Configuration Dialog */}
      <Dialog open={leadTimeConfigOpen} onOpenChange={setLeadTimeConfigOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-barlow font-bold uppercase">
              <Settings className="h-5 w-5 inline mr-2" />
              Default Lead Times Configuration
            </DialogTitle>
            <DialogDescription>
              Set default lead times (in days) for different information categories. These apply when creating new IRS items.
            </DialogDescription>
          </DialogHeader>

          {leadTimeConfig && (
            <div className="space-y-6 py-4">
              {/* Structural */}
              <div>
                <h4 className="text-sm font-bold text-slate-900 uppercase mb-3">Structural</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Structural Drawings</Label>
                    <Input
                      type="number"
                      min="1"
                      value={leadTimeConfig.structural_drawings || 15}
                      onChange={(e) => setLeadTimeConfig({...leadTimeConfig, structural_drawings: parseInt(e.target.value) || 15})}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Reinforcement Details</Label>
                    <Input
                      type="number"
                      min="1"
                      value={leadTimeConfig.reinforcement_details || 10}
                      onChange={(e) => setLeadTimeConfig({...leadTimeConfig, reinforcement_details: parseInt(e.target.value) || 10})}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Pour Sequences</Label>
                    <Input
                      type="number"
                      min="1"
                      value={leadTimeConfig.pour_sequences || 7}
                      onChange={(e) => setLeadTimeConfig({...leadTimeConfig, pour_sequences: parseInt(e.target.value) || 7})}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Foundation Details</Label>
                    <Input
                      type="number"
                      min="1"
                      value={leadTimeConfig.foundation_details || 15}
                      onChange={(e) => setLeadTimeConfig({...leadTimeConfig, foundation_details: parseInt(e.target.value) || 15})}
                      className="h-8"
                    />
                  </div>
                </div>
              </div>

              {/* Architectural */}
              <div>
                <h4 className="text-sm font-bold text-slate-900 uppercase mb-3">Architectural</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Floor Plans</Label>
                    <Input
                      type="number"
                      min="1"
                      value={leadTimeConfig.floor_plans || 15}
                      onChange={(e) => setLeadTimeConfig({...leadTimeConfig, floor_plans: parseInt(e.target.value) || 15})}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Sections & Elevations</Label>
                    <Input
                      type="number"
                      min="1"
                      value={leadTimeConfig.sections_elevations || 15}
                      onChange={(e) => setLeadTimeConfig({...leadTimeConfig, sections_elevations: parseInt(e.target.value) || 15})}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Finishes Layouts</Label>
                    <Input
                      type="number"
                      min="1"
                      value={leadTimeConfig.finishes_layouts || 12}
                      onChange={(e) => setLeadTimeConfig({...leadTimeConfig, finishes_layouts: parseInt(e.target.value) || 12})}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Door/Window Schedules</Label>
                    <Input
                      type="number"
                      min="1"
                      value={leadTimeConfig.door_window_schedules || 10}
                      onChange={(e) => setLeadTimeConfig({...leadTimeConfig, door_window_schedules: parseInt(e.target.value) || 10})}
                      className="h-8"
                    />
                  </div>
                </div>
              </div>

              {/* MEP */}
              <div>
                <h4 className="text-sm font-bold text-slate-900 uppercase mb-3">MEP Services</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Electrical Layouts</Label>
                    <Input
                      type="number"
                      min="1"
                      value={leadTimeConfig.electrical_layouts || 12}
                      onChange={(e) => setLeadTimeConfig({...leadTimeConfig, electrical_layouts: parseInt(e.target.value) || 12})}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Mechanical Layouts</Label>
                    <Input
                      type="number"
                      min="1"
                      value={leadTimeConfig.mechanical_layouts || 15}
                      onChange={(e) => setLeadTimeConfig({...leadTimeConfig, mechanical_layouts: parseInt(e.target.value) || 15})}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Plumbing Layouts</Label>
                    <Input
                      type="number"
                      min="1"
                      value={leadTimeConfig.plumbing_layouts || 12}
                      onChange={(e) => setLeadTimeConfig({...leadTimeConfig, plumbing_layouts: parseInt(e.target.value) || 12})}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Fire Layouts</Label>
                    <Input
                      type="number"
                      min="1"
                      value={leadTimeConfig.fire_layouts || 15}
                      onChange={(e) => setLeadTimeConfig({...leadTimeConfig, fire_layouts: parseInt(e.target.value) || 15})}
                      className="h-8"
                    />
                  </div>
                </div>
              </div>

              {/* Coordination */}
              <div>
                <h4 className="text-sm font-bold text-slate-900 uppercase mb-3">Coordination</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Services Coordination</Label>
                    <Input
                      type="number"
                      min="1"
                      value={leadTimeConfig.services_coordination || 20}
                      onChange={(e) => setLeadTimeConfig({...leadTimeConfig, services_coordination: parseInt(e.target.value) || 20})}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Builders Work</Label>
                    <Input
                      type="number"
                      min="1"
                      value={leadTimeConfig.builders_work || 15}
                      onChange={(e) => setLeadTimeConfig({...leadTimeConfig, builders_work: parseInt(e.target.value) || 15})}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Default (Other)</Label>
                    <Input
                      type="number"
                      min="1"
                      value={leadTimeConfig.default || 15}
                      onChange={(e) => setLeadTimeConfig({...leadTimeConfig, default: parseInt(e.target.value) || 15})}
                      className="h-8"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setLeadTimeConfigOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button onClick={saveLeadTimeConfig} disabled={savingLeadTimes} className="rounded-sm bg-slate-900 hover:bg-slate-800">
              {savingLeadTimes ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Lead Times
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Received Dialog */}
      <Dialog open={markReceivedOpen} onOpenChange={(open) => {
        if (!open) { setMarkReceivedItem(null); setReceivedDate(''); setReceivedNotes(''); setReceivedType('received'); }
        setMarkReceivedOpen(open);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-barlow font-bold uppercase">
              <CheckCircle2 className="h-5 w-5 inline mr-2 text-green-600" />
              Mark Item Received
            </DialogTitle>
            <DialogDescription>
              {markReceivedItem?.description || markReceivedItem?.information_required || 'Record receipt of this information'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-xs font-bold uppercase">Receipt Type</Label>
              <Select value={receivedType} onValueChange={setReceivedType}>
                <SelectTrigger className="mt-1 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="received">Fully Received</SelectItem>
                  <SelectItem value="partial">Partially Received</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase">Date Received</Label>
              <Input
                type="date"
                value={receivedDate}
                onChange={(e) => setReceivedDate(e.target.value)}
                className="mt-1 rounded-sm"
                data-testid="received-date-input"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase">Notes</Label>
              <Textarea
                value={receivedNotes}
                onChange={(e) => setReceivedNotes(e.target.value)}
                placeholder="Any notes about receipt..."
                className="mt-1 rounded-sm"
                data-testid="received-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkReceivedOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleMarkReceived} className="rounded-sm bg-green-600 hover:bg-green-700" data-testid="confirm-mark-received-btn">
              <CheckCircle2 className="h-4 w-4 mr-1" /> Confirm Received
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Programme Comparison Dialog */}
      <Dialog open={compareDialogOpen} onOpenChange={(open) => { setCompareDialogOpen(open); if (!open) setComparisonResult(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-barlow font-bold uppercase">
              <GitCompare className="h-5 w-5 inline mr-2" />
              Compare Programme Revisions
            </DialogTitle>
            <DialogDescription>
              Select two programme uploads to compare and identify changes
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase">Programme 1 (Baseline)</Label>
                <Select value={compareProgramme1 || ''} onValueChange={setCompareProgramme1}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select programme..." />
                  </SelectTrigger>
                  <SelectContent>
                    {programmeUploads.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase">Programme 2 (Compare)</Label>
                <Select value={compareProgramme2 || ''} onValueChange={setCompareProgramme2}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select programme..." />
                  </SelectTrigger>
                  <SelectContent>
                    {programmeUploads.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              onClick={compareProgrammes}
              disabled={comparing || !compareProgramme1 || !compareProgramme2}
              className="w-full rounded-sm"
            >
              {comparing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <GitCompare className="h-4 w-4 mr-2" />}
              Compare Programmes
            </Button>

            {/* Results */}
            {comparisonResult && (
              <div className="space-y-4 pt-4 border-t">
                {/* Summary */}
                <div className="grid grid-cols-4 gap-2">
                  <div className="bg-green-50 rounded p-3 text-center">
                    <p className="text-2xl font-bold text-green-700">{comparisonResult.summary.total_added}</p>
                    <p className="text-xs text-green-600">Added</p>
                  </div>
                  <div className="bg-red-50 rounded p-3 text-center">
                    <p className="text-2xl font-bold text-red-700">{comparisonResult.summary.total_removed}</p>
                    <p className="text-xs text-red-600">Removed</p>
                  </div>
                  <div className="bg-amber-50 rounded p-3 text-center">
                    <p className="text-2xl font-bold text-amber-700">{comparisonResult.summary.total_modified}</p>
                    <p className="text-xs text-amber-600">Modified</p>
                  </div>
                  <div className="bg-slate-50 rounded p-3 text-center">
                    <p className="text-2xl font-bold text-slate-700">{comparisonResult.summary.total_unchanged}</p>
                    <p className="text-xs text-slate-600">Unchanged</p>
                  </div>
                </div>

                {/* Added Items */}
                {comparisonResult.added_items.length > 0 && (
                  <div>
                    <h4 className="text-sm font-bold text-green-700 mb-2">
                      <Plus className="h-4 w-4 inline mr-1" />
                      Added Items ({comparisonResult.added_items.length})
                    </h4>
                    <div className="max-h-32 overflow-y-auto bg-green-50 rounded p-2 space-y-1">
                      {comparisonResult.added_items.slice(0, 10).map((item, i) => (
                        <p key={i} className="text-xs text-green-800 truncate">+ {item.description}</p>
                      ))}
                      {comparisonResult.added_items.length > 10 && (
                        <p className="text-xs text-green-600 italic">...and {comparisonResult.added_items.length - 10} more</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Removed Items */}
                {comparisonResult.removed_items.length > 0 && (
                  <div>
                    <h4 className="text-sm font-bold text-red-700 mb-2">
                      <Trash2 className="h-4 w-4 inline mr-1" />
                      Removed Items ({comparisonResult.removed_items.length})
                    </h4>
                    <div className="max-h-32 overflow-y-auto bg-red-50 rounded p-2 space-y-1">
                      {comparisonResult.removed_items.slice(0, 10).map((item, i) => (
                        <p key={i} className="text-xs text-red-800 truncate">- {item.description}</p>
                      ))}
                      {comparisonResult.removed_items.length > 10 && (
                        <p className="text-xs text-red-600 italic">...and {comparisonResult.removed_items.length - 10} more</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Modified Items */}
                {comparisonResult.modified_items.length > 0 && (
                  <div>
                    <h4 className="text-sm font-bold text-amber-700 mb-2">
                      <Edit2 className="h-4 w-4 inline mr-1" />
                      Modified Items ({comparisonResult.modified_items.length})
                    </h4>
                    <div className="max-h-48 overflow-y-auto bg-amber-50 rounded p-2 space-y-2">
                      {comparisonResult.modified_items.slice(0, 5).map((item, i) => (
                        <div key={i} className="text-xs border-b border-amber-200 pb-2 last:border-0">
                          <p className="font-medium text-amber-900 truncate">{item.description}</p>
                          {item.changes.map((change, j) => (
                            <p key={j} className="text-amber-700 pl-2">
                              {change.field}: <span className="line-through">{change.old_value}</span> → <span className="font-medium">{change.new_value}</span>
                            </p>
                          ))}
                        </div>
                      ))}
                      {comparisonResult.modified_items.length > 5 && (
                        <p className="text-xs text-amber-600 italic">...and {comparisonResult.modified_items.length - 5} more</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setCompareDialogOpen(false); setComparisonResult(null); }} className="rounded-sm">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Saved Schedule Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={(open) => {
        setShareDialogOpen(open);
        if (open) loadProjectTeamForShare();
      }}>
        <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Send className="h-5 w-5" />
              Issue IRS Schedule
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Send <strong>{selectedIrsSchedule?.schedule_name}</strong> to the selected recipients.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-3">
            {/* Send Type Selector */}
            <div>
              <Label className="text-xs sm:text-sm font-medium">Send Type</Label>
              <div className="grid grid-cols-3 gap-1.5 sm:gap-3 mt-2">
                <Button type="button" variant={shareSendType === 'external' ? 'default' : 'outline'}
                  className={`rounded-sm text-xs px-1 sm:px-3 h-8 sm:h-9 ${shareSendType === 'external' ? 'bg-blue-600' : ''}`}
                  onClick={() => setShareSendType('external')} data-testid="irs-share-type-external">
                  <Mail className="h-3.5 w-3.5 sm:mr-1.5" /><span className="hidden sm:inline">External</span>
                </Button>
                <Button type="button" variant={shareSendType === 'internal' ? 'default' : 'outline'}
                  className={`rounded-sm text-xs px-1 sm:px-3 h-8 sm:h-9 ${shareSendType === 'internal' ? 'bg-blue-600' : ''}`}
                  onClick={() => setShareSendType('internal')} data-testid="irs-share-type-internal">
                  <Building2 className="h-3.5 w-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Internal</span>
                </Button>
                <Button type="button" variant={shareSendType === 'both' ? 'default' : 'outline'}
                  className={`rounded-sm text-xs px-1 sm:px-3 h-8 sm:h-9 ${shareSendType === 'both' ? 'bg-green-600' : ''}`}
                  onClick={() => setShareSendType('both')} data-testid="irs-share-type-both">
                  <Users className="h-3.5 w-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Both</span>
                </Button>
              </div>
              <p className="text-[10px] sm:text-xs text-slate-500 mt-1">
                {shareSendType === 'external' ? 'Send to external consultants/sub-contractors'
                  : shareSendType === 'internal' ? 'Send to internal team members'
                  : 'Send to both internal team and external consultants'}
              </p>
            </div>

            {/* Project Team Members */}
            {projectTeamMembers.length > 0 && (
              <div className="p-2 sm:p-3 bg-slate-50 rounded-sm border border-slate-200">
                <Label className="text-xs sm:text-sm font-medium mb-2 block">
                  {shareSendType === 'internal' ? 'Internal Team' : shareSendType === 'external' ? 'Consultants & Subcontractors' : 'All Contacts'}
                </Label>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {projectTeamMembers
                    .filter(m => {
                      if (shareSendType === 'internal') return m.is_internal;
                      if (shareSendType === 'external') return m.is_consultant || m.is_subcontractor;
                      return true;
                    })
                    .map((m, idx) => {
                      const isAdded = shareRecipients.some(r => r.email === m.email);
                      return (
                        <div key={idx} className="flex items-center justify-between text-xs p-1 rounded hover:bg-white gap-1">
                          <div className="min-w-0 flex-1">
                            <span className="font-medium">{m.name}</span>
                            <span className="text-slate-400 ml-1 text-[10px] hidden sm:inline">{m.email}</span>
                            {m.role && <Badge className="ml-1 text-[8px] sm:text-[9px] h-4 rounded-sm">{m.role}</Badge>}
                          </div>
                          <Button size="sm" variant={isAdded ? 'ghost' : 'outline'} className="h-6 text-[10px] rounded-sm shrink-0" disabled={isAdded}
                            onClick={() => setShareRecipients(prev => [...prev, { name: m.name, email: m.email, role: m.role }])}>
                            {isAdded ? 'Added' : 'Add'}
                          </Button>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Manual add recipient */}
            <div className="flex flex-col sm:grid sm:grid-cols-3 gap-1.5 sm:gap-2">
              <Input value={shareName} onChange={e => setShareName(e.target.value)} placeholder="Name" className="h-8 text-xs rounded-sm" data-testid="irs-share-name" />
              <Input value={shareEmail} onChange={e => setShareEmail(e.target.value)} placeholder="Email" className="h-8 text-xs rounded-sm" type="email" data-testid="irs-share-email" />
              <div className="flex gap-1">
                <Input value={shareRole} onChange={e => setShareRole(e.target.value)} placeholder="Role" className="h-8 text-xs rounded-sm flex-1" data-testid="irs-share-role" />
                <Button size="sm" variant="outline" className="h-8 text-xs rounded-sm px-2" onClick={() => {
                  if (!shareEmail.trim()) return;
                  setShareRecipients(prev => [...prev, { name: shareName, email: shareEmail, role: shareRole }]);
                  setShareName(''); setShareEmail(''); setShareRole('');
                }} data-testid="irs-share-add-btn">+</Button>
              </div>
            </div>

            {/* Selected recipients */}
            {shareRecipients.length > 0 && (
              <div className="p-2 sm:p-3 bg-slate-50 rounded-sm border border-slate-200">
                <Label className="text-xs sm:text-sm font-medium mb-2 block">Recipients ({shareRecipients.length})</Label>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {shareRecipients.map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-xs p-1 bg-white rounded-sm gap-1">
                      <div className="min-w-0 flex-1 truncate">
                        <span className="font-medium">{r.name || r.email}</span>
                        <span className="text-slate-400 ml-1 hidden sm:inline">{r.name ? r.email : ''}</span>
                        {r.role && <Badge className="ml-1 text-[9px] h-4 rounded-sm">{r.role}</Badge>}
                      </div>
                      <button className="text-red-400 hover:text-red-600 shrink-0" onClick={() => setShareRecipients(prev => prev.filter((_, j) => j !== i))}>
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Send email checkbox */}
            <div className="flex items-center gap-2">
              <input type="checkbox" id="irs_share_send_email" checked={shareSendEmail}
                onChange={(e) => setShareSendEmail(e.target.checked)} className="rounded" data-testid="irs-share-send-email" />
              <Label htmlFor="irs_share_send_email" className="text-xs sm:text-sm">Open email client with schedule details</Label>
            </div>

            {/* Info panel */}
            <div className="p-2 sm:p-3 bg-blue-50 rounded-sm border border-blue-200">
              <p className="text-xs sm:text-sm text-blue-700 font-medium">What happens when you issue:</p>
              <ul className="text-[10px] sm:text-xs text-blue-600 mt-1 space-y-0.5">
                <li>Schedule shared with selected recipients</li>
                <li>Email sent with schedule details attached</li>
                <li>Activity logged in communication history</li>
              </ul>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => { setShareDialogOpen(false); setShareRecipients([]); }}>Cancel</Button>
            <Button className="rounded-sm bg-blue-600 hover:bg-blue-700"
              disabled={shareRecipients.length === 0 || sharingInProgress}
              onClick={async () => {
                if (!selectedIrsSchedule) return;
                setSharingInProgress(true);
                try {
                  await api.post(`/projects/${projectId}/schedule/${selectedIrsSchedule.id}/share`, {
                    recipients: shareRecipients.map(r => ({ name: r.name, email: r.email, role: r.role, permission: 'view' })),
                    send_type: shareSendType,
                    send_email: shareSendEmail
                  });
                  if (shareSendEmail) {
                    const toEmails = shareRecipients.map(r => r.email).join(',');
                    const subject = encodeURIComponent(`${selectedIrsSchedule.schedule_name} - IRS Schedule`);
                    const body = encodeURIComponent(
                      `Dear all,\n\nPlease find the Information Required Schedule details below:\n\n` +
                      `Schedule: ${selectedIrsSchedule.schedule_name}\n` +
                      `Trade: ${selectedIrsSchedule.trade_package || '-'}\n` +
                      `Status: ${selectedIrsSchedule.status}\n` +
                      `Items: ${scheduleItems.length}\n` +
                      `Rev: ${selectedIrsSchedule.revision || 0}\n\n` +
                      `View online: ${window.location.href}\n\n` +
                      `Kind regards`
                    );
                    window.open(`mailto:${toEmails}?subject=${subject}&body=${body}`, '_blank');
                  }
                  toast.success(`Schedule issued to ${shareRecipients.length} recipients`);
                  setShareDialogOpen(false);
                  setShareRecipients([]);
                } catch { toast.error('Failed to issue schedule'); }
                finally { setSharingInProgress(false); }
              }}
              data-testid="irs-confirm-share-btn"
            >
              {sharingInProgress ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Issuing...</>
              ) : (
                <><Send className="h-3.5 w-3.5 mr-1" />Issue Schedule ({shareRecipients.length})</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Published IRS Items Dialog */}
      <Dialog open={sharePublishedOpen} onOpenChange={setSharePublishedOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Send className="h-5 w-5" />
              Share Published IRS Items
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              {sharePublishedItems.length === 1
                ? <>Send <strong>{sharePublishedItems[0]?.description || sharePublishedItems[0]?.information_required || 'Item'}</strong> to selected recipients.</>
                : <>Send {sharePublishedItems.length} IRS items to selected recipients.</>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-3">
            {/* Send Type */}
            <div>
              <Label className="text-xs sm:text-sm font-medium">Send Type</Label>
              <div className="grid grid-cols-3 gap-1.5 sm:gap-3 mt-2">
                <Button type="button" variant={sharePubSendType === 'external' ? 'default' : 'outline'}
                  className={`rounded-sm text-xs px-1 sm:px-3 h-8 sm:h-9 ${sharePubSendType === 'external' ? 'bg-blue-600' : ''}`}
                  onClick={() => setSharePubSendType('external')} data-testid="irs-pub-share-type-external">
                  <Mail className="h-3.5 w-3.5 sm:mr-1.5" /><span className="hidden sm:inline">External</span>
                </Button>
                <Button type="button" variant={sharePubSendType === 'internal' ? 'default' : 'outline'}
                  className={`rounded-sm text-xs px-1 sm:px-3 h-8 sm:h-9 ${sharePubSendType === 'internal' ? 'bg-blue-600' : ''}`}
                  onClick={() => setSharePubSendType('internal')} data-testid="irs-pub-share-type-internal">
                  <Building2 className="h-3.5 w-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Internal</span>
                </Button>
                <Button type="button" variant={sharePubSendType === 'both' ? 'default' : 'outline'}
                  className={`rounded-sm text-xs px-1 sm:px-3 h-8 sm:h-9 ${sharePubSendType === 'both' ? 'bg-green-600' : ''}`}
                  onClick={() => setSharePubSendType('both')} data-testid="irs-pub-share-type-both">
                  <Users className="h-3.5 w-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Both</span>
                </Button>
              </div>
            </div>

            {/* Team Members */}
            {projectTeamMembers.length > 0 && (
              <div className="p-2 sm:p-3 bg-slate-50 rounded-sm border border-slate-200">
                <Label className="text-xs sm:text-sm font-medium mb-2 block">
                  {sharePubSendType === 'internal' ? 'Internal Team' : sharePubSendType === 'external' ? 'Consultants & Subcontractors' : 'All Contacts'}
                </Label>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {projectTeamMembers
                    .filter(m => {
                      if (sharePubSendType === 'internal') return m.is_internal;
                      if (sharePubSendType === 'external') return m.is_consultant || m.is_subcontractor;
                      return true;
                    })
                    .map((m, idx) => {
                      const isAdded = sharePubRecipients.some(r => r.email === m.email);
                      return (
                        <div key={idx} className="flex items-center justify-between text-xs p-1 rounded hover:bg-white gap-1">
                          <div className="min-w-0 flex-1">
                            <span className="font-medium">{m.name}</span>
                            <span className="text-slate-400 ml-1 text-[10px] hidden sm:inline">{m.email}</span>
                            {m.role && <Badge className="ml-1 text-[8px] sm:text-[9px] h-4 rounded-sm">{m.role}</Badge>}
                          </div>
                          <Button size="sm" variant={isAdded ? 'ghost' : 'outline'} className="h-6 text-[10px] rounded-sm shrink-0" disabled={isAdded}
                            onClick={() => setSharePubRecipients(prev => [...prev, { name: m.name, email: m.email, role: m.role }])}>
                            {isAdded ? 'Added' : 'Add'}
                          </Button>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Manual add */}
            <div className="flex flex-col sm:grid sm:grid-cols-3 gap-1.5 sm:gap-2">
              <Input value={sharePubName} onChange={e => setSharePubName(e.target.value)} placeholder="Name" className="h-8 text-xs rounded-sm" data-testid="irs-pub-share-name" />
              <Input value={sharePubEmail} onChange={e => setSharePubEmail(e.target.value)} placeholder="Email" className="h-8 text-xs rounded-sm" type="email" data-testid="irs-pub-share-email" />
              <div className="flex gap-1">
                <Input value={sharePubRole} onChange={e => setSharePubRole(e.target.value)} placeholder="Role" className="h-8 text-xs rounded-sm flex-1" data-testid="irs-pub-share-role" />
                <Button size="sm" variant="outline" className="h-8 text-xs rounded-sm px-2" onClick={() => {
                  if (!sharePubEmail.trim()) return;
                  setSharePubRecipients(prev => [...prev, { name: sharePubName, email: sharePubEmail, role: sharePubRole }]);
                  setSharePubName(''); setSharePubEmail(''); setSharePubRole('');
                }} data-testid="irs-pub-share-add-btn">+</Button>
              </div>
            </div>

            {/* Selected recipients */}
            {sharePubRecipients.length > 0 && (
              <div className="p-2 sm:p-3 bg-slate-50 rounded-sm border border-slate-200">
                <Label className="text-xs sm:text-sm font-medium mb-2 block">Recipients ({sharePubRecipients.length})</Label>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {sharePubRecipients.map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-xs p-1 bg-white rounded-sm gap-1">
                      <div className="min-w-0 flex-1 truncate">
                        <span className="font-medium">{r.name || r.email}</span>
                        <span className="text-slate-400 ml-1 hidden sm:inline">{r.name ? r.email : ''}</span>
                        {r.role && <Badge className="ml-1 text-[9px] h-4 rounded-sm">{r.role}</Badge>}
                      </div>
                      <button className="text-red-400 hover:text-red-600 shrink-0" onClick={() => setSharePubRecipients(prev => prev.filter((_, j) => j !== i))}>
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Send email checkbox */}
            <div className="flex items-center gap-2">
              <input type="checkbox" id="irs_pub_share_email" checked={sharePubSendEmail}
                onChange={(e) => setSharePubSendEmail(e.target.checked)} className="rounded" data-testid="irs-pub-share-send-email" />
              <Label htmlFor="irs_pub_share_email" className="text-xs sm:text-sm">Open email client with IRS details</Label>
            </div>

            {/* Info */}
            <div className="p-2 sm:p-3 bg-blue-50 rounded-sm border border-blue-200">
              <p className="text-xs sm:text-sm text-blue-700 font-medium">What happens when you share:</p>
              <ul className="text-[10px] sm:text-xs text-blue-600 mt-1 space-y-0.5">
                <li>IRS items shared with selected recipients</li>
                <li>Email sent with schedule details</li>
                <li>Activity logged in communication history</li>
              </ul>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-sm text-xs sm:text-sm" onClick={() => setSharePublishedOpen(false)}>Cancel</Button>
            <Button className="rounded-sm bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm"
              disabled={sharePubRecipients.length === 0 || sharePubInProgress}
              onClick={handleSharePublished} data-testid="irs-confirm-pub-share-btn">
              {sharePubInProgress ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sharing...</>
              ) : (
                <><Send className="h-3.5 w-3.5 mr-1" />Share ({sharePubRecipients.length})</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      </div>
    </DashboardLayout>
  );
}